import moment from 'moment';

export const B2C_VERSION = '0.0.1';

export const requiredSurveys = [
  // 필수설문 코드 목록
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'h',
  'i',
  'j',
  'k',
  'l',
  'm',
  'A',
];

export const requiredYnSurveys = [
  // 증상 유무 포맷이 달라서 꼭 선택을 해야하는 화면
  'k',
];

export const exceptYnSurveys = [
  // 예/아니오를 건너뛰는 설문 코드 목록
  'B', // 첩약건보 - 월경통
  'C', // 첩약건보 - 안면신경마비
  'D', // 첩약건보 - 뇌혈관질환 후유증
  'd',
  'f',
  'n',
  'o',
  'p',
  'q',
  'r',
  's',
  't',
  'u',
  'v',
  'w',
  'x',
  'y',
  'z',
];

// 첩약건보 설문 코드별로 생략하는 필수 설문 코드 Map
export const omitReqSurveyCdByIsrcSurveyCd = new Map([
  ['B', ['i', 'k', 'l', 'm', 'A']], // 월경통 - 남성, 월경불임, 갱년기유방, 소아, 피로면역력 제외
  ['C', ['i', 'j', 'k', 'l', 'm', 'A']], // 안면신경마비 - 남성, 월경불임, 임신/출산, 갱년기유방, 소아, 피로면역력 제외
  ['D', ['i', 'j', 'k', 'l', 'm', 'A']], // 뇌혈관질환 후유증 - 남성, 월경불임, 임신/출산, 갱년기유방, 소아, 피로면역력 제외
  ['E', ['i', 'j', 'k', 'l', 'm', 'A']], // 요추추간판탈출증 - 남성, 월경불임, 임신/출산, 갱년기유방, 소아, 피로면역력 제외
  ['F', ['i', 'j', 'k', 'l', 'm', 'A']], // 알레르기비염 - 남성, 월경불임, 임신/출산, 갱년기유방, 소아, 피로면역력 제외
  ['G', ['a', 'i', 'j', 'k', 'l', 'm', 'A']], // 기능성 소화불량 - 소화, 남성, 월경불임, 임신/출산, 갱년기유방, 소아, 피로면역력 제외
]);

export const noYnSurveys = [
  // 예/아니오 화면이 없는 설문 코드 목록
  'i',
  'k',
  'l',
  'm',
];

export enum Gender {
  Male = 'MALE',
  Female = 'FEMALE',
}

export enum Page {
  Continue,
  Init,
  Term,
  ModeSelect,
  MainCategory,
  MainSymptom,
  DetailSurvey,
  OptionalSurveys,
  CheerUp,
  Confirm,
  Review,
  End,
  NotFoundError,
  Expired,
  Completed,
  ServiceTerm,
  PersonalInfoTerm,
  TreatmentIsProgressing,
  B2CMain,
  B2CForm,
  B2CAdditionalSurvey,
  B2CResult,
  B2CSolution,
}

export enum SelectType {
  Radio,
  Check,
  Scale, // scale을 묻는 세부 설문 (말단)
  Onset, // onset을 묻는 세부 설문 (말단)
  Date, // 날짜를 묻는 설문
  NumberInput, // NumberInput 으로 숫자를 입력받는 설문
}

export enum SurveyCategory {
  Main, // 대분류 증상 여부 설문 화면
  Middle, // 중분류 증상 여부 설문 화면
  End, // 마지막 depth 설문 화면
}

export enum ChartType {
  CC,
  PI,
  OS,
  FH,
}

interface ISurvey {
  [key: string]: TSurvey;
}

export type TAnswer = {
  label: string;
  next?: string | string[];
  value: number | string;
  symptomCode?: string[];
  mustShowFlagInYejinSymptomTable?: boolean;
};

export type TSurvey = {
  category: SurveyCategory;
  selectType: SelectType;
  chartType?: ChartType;
  postfix?: string; // selectType이 NumberInput일 때 postfix
  defaultDate?: string; // selectType이 Date일 때 defaultDate
  defaultValue?: number; // selectType이 NumberInput일 때 defaultValue
  label?: string;
  desc?: string[];
  answers?: TAnswer[];
  children?: ISurvey;
  deprecated?: boolean;
};

export type TParsedSurvey = {
  formKey: string;
  category: SurveyCategory;
  selectType: SelectType;
  chartType?: ChartType;
  postfix?: string; // selectType이 NumberInput일 때 postfix
  defaultDate?: string; // selectType이 Date일 때 defaultDate
  defaultValue?: number; // selectType이 NumberInput일 때 defaultValue
  label: string;
  desc: string[];
  answers: TAnswer[];
  childrenKeys?: string[];
  deprecated?: boolean;
};

export const insuranceSurveyObj: ISurvey = {
  B: {
    category: SurveyCategory.Main,
    label: '월경통',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      {
        label: '네, 있어요.',
        next: ['Be', 'Bi', 'Bk', 'Ba', 'Bj', 'Bb', 'Bc', 'Bf', 'Bh'],
        value: 1,
      },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      Be: {
        category: SurveyCategory.Middle,
        label: '월경통 정도',
        desc: ['통증 지속시간은 어느정도인가요?'],
        selectType: SelectType.Radio,
        answers: [
          { label: '생리 시작 후 2-3일 이내 지속', value: 'Bea', next: 'Bea', symptomCode: ['S308'] },
          { label: '생리 시작 후 4일 이상 지속', value: 'Beb', next: 'Bea', symptomCode: ['S308'] },
        ],
        chartType: ChartType.CC,
        children: {
          Bea: {
            category: SurveyCategory.End,
            selectType: SelectType.Scale,
          },
        },
      },
      Bi: {
        category: SurveyCategory.End,
        label: '월경통 발병일',
        desc: ['현재와 같은 정도의', '월경통 발병시기는 언제인가요?'],
        chartType: ChartType.OS,
        selectType: SelectType.Radio,
        answers: [
          { label: '1개월 이내(최근 월경)', value: 1, symptomCode: ['S000'] },
          { label: '1-2년 이내', value: 2, symptomCode: ['S000'] },
          { label: '수년 전', value: 3, symptomCode: ['S207'] },
          { label: '출산 후', value: 4, symptomCode: ['S000'] },
        ],
      },
      Bk: {
        category: SurveyCategory.End,
        label: '최근 증상 발현일',
        desc: ['최근 월경통 증상이 나타난 날은 언제인가요?'],
        selectType: SelectType.Date,
        chartType: ChartType.OS,
        defaultDate: moment().format('YYYY-MM-DD'),
      },
      Ba: {
        category: SurveyCategory.End,
        label: '최근 월경 시작일',
        desc: ['마지막 월경 시작일은 언제인가요?'],
        selectType: SelectType.Date,
        chartType: ChartType.PI,
        defaultDate: moment().format('YYYY-MM-DD'),
      },
      Bj: {
        category: SurveyCategory.End,
        label: '월경 주기',
        desc: ['월경 평균 주기를 입력하세요.'],
        selectType: SelectType.NumberInput,
        chartType: ChartType.PI,
        defaultValue: 28,
        postfix: '일',
      },
      Bb: {
        category: SurveyCategory.End,
        label: '월경 기간',
        desc: ['월경 평균 기간을 입력하세요.'],
        selectType: SelectType.NumberInput,
        chartType: ChartType.PI,
        defaultValue: 5,
        postfix: '일',
      },
      Bc: {
        category: SurveyCategory.Middle,
        label: '월경 동반 증상',
        desc: ['월경 관련하여 불편한 증상을 모두 선택하세요.'],
        selectType: SelectType.Check,
        chartType: ChartType.CC,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '두통', value: 'Bca', next: 'Bca', symptomCode: ['S087'] },
          { label: '메슥거림, 구역감', value: 'Bcb', next: 'Bcb', symptomCode: ['S040'] },
          { label: '구토(먹은 음식물을 토함)', value: 'Bcc', next: 'Bcc', symptomCode: ['S044'] },
          { label: '피로', value: 'Bcd', next: 'Bcd', symptomCode: ['S056'] },
          { label: '설사', value: 'Bce', next: 'Bce', symptomCode: ['S159'] },
          { label: '변비', value: 'Bcf', next: 'Bcf', symptomCode: ['S126'] },
          { label: '실신', value: 'Bcg', next: 'Bcg', symptomCode: ['S198'] },
          { label: '우울', value: 'Bch', next: 'Bch', symptomCode: ['S227'] },
          { label: '부종', value: 'Bci', next: 'Bci', symptomCode: ['S135'] },
          { label: '월경 주기가 평소보다 길어졌어요.', value: 10, symptomCode: ['S233'] },
          { label: '월경 주기가 평소보다 짧아졌어요.', value: 11, symptomCode: ['S770'] },
          { label: '월경 전 복통이 있어요.', value: 13, symptomCode: ['S017'] },
          { label: '월경 전 유방통증이 있어요.', value: 14, symptomCode: ['S018'] },
          { label: '월경 양이 많아졌어요.', value: 15, symptomCode: ['S230'] },
          { label: '월경 양이 적어졌어요.', value: 16, symptomCode: ['S800'] },
          { label: '월경 색이 진하고 덩어리가 많아요.', value: 17, symptomCode: ['S232'] },
          {
            label: '부정출혈(월경기간이 아닐 때 발생하는 출혈)이 있어요.',
            value: 18,
            symptomCode: ['S142'],
          },
        ],
        children: {
          Bca: {
            category: SurveyCategory.End,
            selectType: SelectType.Scale,
          },
          Bcb: {
            category: SurveyCategory.End,
            selectType: SelectType.Scale,
          },
          Bcc: {
            category: SurveyCategory.End,
            selectType: SelectType.Scale,
          },
          Bcd: {
            category: SurveyCategory.End,
            selectType: SelectType.Scale,
          },
          Bce: {
            category: SurveyCategory.End,
            selectType: SelectType.Scale,
          },
          Bcf: {
            category: SurveyCategory.End,
            selectType: SelectType.Scale,
          },
          Bcg: {
            category: SurveyCategory.End,
            selectType: SelectType.Scale,
          },
          Bch: {
            category: SurveyCategory.End,
            selectType: SelectType.Scale,
          },
          Bci: {
            category: SurveyCategory.End,
            selectType: SelectType.Scale,
          },
        },
      },
      Bf: {
        category: SurveyCategory.Middle,
        label: '복용약',
        desc: ['증상 완화를 위해 복용 중인 약이 있으신가요?'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '약을 복용하지 않아요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '진통제(NSAIDs, 타이레놀 등)', value: 'Bfa', next: 'Bfa', symptomCode: ['S000'] },
          { label: '경구피임약', value: 2, symptomCode: ['S000'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          Bfa: {
            category: SurveyCategory.End,
            label: '진통제 효과 여부',
            desc: ['진통제를 복용하면 증상이 호전되시나요?'],
            selectType: SelectType.Radio,
            answers: [
              { label: '호전돼요.', value: 1, symptomCode: ['S000'] },
              { label: '효과가 없어요.', value: 2, symptomCode: ['S000'] },
            ],
          },
        },
      },
      Bh: {
        category: SurveyCategory.Middle,
        label: '여성 질환',
        desc: ['진단 받거나 앓고 있는', '여성 질환이 있으신가요?'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '진단 받은 여성 질환이 없어요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '다낭성난소증후군', value: 'Bha', next: 'Bha', symptomCode: ['S000'] },
          { label: '자궁근종', value: 'Bhb', next: 'Bhb', symptomCode: ['S130'] },
          { label: '자궁내막증', value: 'Bhc', next: 'Bhc', symptomCode: ['S130'] },
          { label: '자궁선근증', value: 'Bhd', next: 'Bhd', symptomCode: ['S130'] },
          { label: '자궁 내 신생물, 물혹 등', value: 'Bhe', next: 'Bhe', symptomCode: ['S130'] },
          { label: '자궁하수', value: 'Bhf', next: 'Bhf', symptomCode: ['S260'] },
          { label: '난소낭종', value: 'Bhg', next: 'Bhg', symptomCode: ['S130'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          Bha: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Bhb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Bhc: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Bhd: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Bhe: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Bhf: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Bhg: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
  C: {
    category: SurveyCategory.Main,
    label: '안면마비',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      {
        label: '네, 있어요.',
        next: ['Ca', 'Cb', 'Cc', 'Cd', 'Ce'],
        value: 1,
      },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      Ca: {
        category: SurveyCategory.End,
        label: '안면마비 부위',
        desc: ['어느 쪽 얼굴에 마비감이 느껴지시나요?'],
        chartType: ChartType.CC,
        selectType: SelectType.Radio,
        answers: [
          { label: '왼쪽', value: 1, symptomCode: ['S039'] },
          { label: '오른쪽', value: 2, symptomCode: ['S039'] },
          { label: '양쪽', value: 3, symptomCode: ['S039'] },
        ],
      },
      Cb: {
        category: SurveyCategory.End,
        label: '안면마비 발병일',
        desc: ['증상이 나타나기 시작한 날짜는 언제인가요?'],
        selectType: SelectType.Date,
        chartType: ChartType.OS,
        defaultDate: moment().format('YYYY-MM-DD'),
      },
      Cc: {
        category: SurveyCategory.End,
        label: '안면마비 동반 증상',
        desc: ['안면 근육 마비감 외에', '불편한 증상을 모두 선택하세요.'],
        selectType: SelectType.Check,
        chartType: ChartType.CC,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '귀 뒤에 통증이 있어요.', value: 1, symptomCode: ['S000'] },
          { label: '귀에서 소리가 나요.', value: 2, symptomCode: ['S250'] },
          { label: '귀 주위에 수포가 있어요.', value: 3, symptomCode: ['S000'] },
          { label: '외부의 큰소리가 통증으로 느껴져요.', value: 4, symptomCode: ['S000'] },
          { label: '내 말소리가 크게 울려서 들려요.', value: 5, symptomCode: ['S000'] },
          { label: '맛이 잘 안느껴져요.', value: 6, symptomCode: ['S000'] },
          { label: '눈이 건조해요.', value: 7, symptomCode: ['S000'] },
          { label: '눈물이 흘러요.', value: 8, symptomCode: ['S068'] },
          { label: '두통', value: 9, symptomCode: ['S087'] },
          { label: '어지럼증', value: 10, symptomCode: ['S325'] },
          { label: '음식을 먹을 때 눈물이 나요.', value: 11, symptomCode: ['S000'] },
          { label: '입과 눈 근육이 함께 움직여요.', value: 12, symptomCode: ['S000'] },
        ],
      },
      Cd: {
        category: SurveyCategory.Middle,
        label: '치료 내역',
        desc: ['안면마비 증상으로', '병/의원을 방문한 적이 있나요?'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '치료받은 적 없어요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '병/의원에서 치료 받았어요.', value: 'Cda', next: 'Cda', symptomCode: ['S000'] },
          { label: '한방병/의원에서 치료 받았어요.', value: 2, symptomCode: ['S000'] },
        ],
        children: {
          Cda: {
            category: SurveyCategory.End,
            label: '치료 내역',
            desc: ['처방받은 약이나 검사를 모두 선택하세요.'],
            selectType: SelectType.Check,
            chartType: ChartType.PI,
            answers: [
              { label: '자세한 내역은 모르겠어요.', value: 1, symptomCode: ['S000'] },
              { label: '스테로이드', value: 2, symptomCode: ['S000'] },
              { label: '항바이러스제', value: 3, symptomCode: ['S000'] },
              { label: '안연고', value: 4, symptomCode: ['S000'] },
              { label: '주사, 수액', value: 5, symptomCode: ['S000'] },
              { label: '뇌 CT', value: 6, symptomCode: ['S000'] },
              { label: '뇌 MRI', value: 7, symptomCode: ['S000'] },
              { label: '근전도검사/신경전도검사', value: 8, symptomCode: ['S000'] },
            ],
          },
        },
      },
      Ce: {
        category: SurveyCategory.End,
        label: '안면마비 발병 요인',
        desc: ['다음 중 최근 2주간 해당되는 사항을 모두 선택하세요.'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '특별한 요인이 없었어요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '피로, 과로', value: 1, symptomCode: ['S056'] },
          { label: '수면 불량', value: 2, symptomCode: ['S138'] },
          { label: '스트레스', value: 3, symptomCode: ['S000'] },
          { label: '소화불량', value: 4, symptomCode: ['S178'] },
          { label: '식욕부진', value: 5, symptomCode: ['S192'] },
          { label: '체중 감소', value: 6, symptomCode: ['S000'] },
          { label: '감기, 장염 등 바이러스 질환', value: 7, symptomCode: ['S000'] },
          { label: '추운 곳에 장시간 노출', value: 8, symptomCode: ['S000'] },
          { label: '수술', value: 9, symptomCode: ['S000'] },
        ],
      },
    },
  },
  D: {
    category: SurveyCategory.Main,
    label: '뇌혈관질환 후유증',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      {
        label: '네, 있어요.',
        next: ['Da', 'Db', 'Dh', 'De', 'Df', 'Dg'],
        value: 1,
      },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      Da: {
        category: SurveyCategory.End,
        label: '뇌혈관질환',
        desc: ['진단받은 뇌혈관질환은 무엇인가요?'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '뇌경색', value: 2, symptomCode: ['S707'] },
          { label: '뇌출혈', value: 3, symptomCode: ['S707'] },
          { label: '진단명을 모르겠어요.', value: 1, symptomCode: ['S000'] },
        ],
      },
      Db: {
        category: SurveyCategory.End,
        label: '뇌혈관질환 발병일',
        desc: ['최초 증상 발생일은 언제인가요?'],
        selectType: SelectType.Date,
        chartType: ChartType.OS,
        defaultDate: moment().format('YYYY-MM-DD'),
      },
      Dh: {
        category: SurveyCategory.Middle,
        label: '재발/수술 이력',
        desc: ['해당되는 사항을 모두 선택하세요.'],
        chartType: ChartType.PI,
        selectType: SelectType.Check,
        answers: [
          { label: '해당 사항이 없어요.', value: 0 },
          { label: '재발한 적이 있어요.', value: 'Dha', next: 'Dha', symptomCode: ['S707'] },
          { label: '수술받은 적이 있어요.', value: 'Dhb', next: 'Dhb', symptomCode: ['S707'] },
        ],
        children: {
          Dha: {
            category: SurveyCategory.End,
            label: '최근 재발일',
            desc: ['최근 재발일은 언제인가요?'],
            selectType: SelectType.Date,
            chartType: ChartType.OS,
            defaultDate: moment().format('YYYY-MM-DD'),
          },
          Dhb: {
            category: SurveyCategory.End,
            label: '최근 수술일',
            desc: ['최근 수술일은 언제인가요?'],
            selectType: SelectType.Date,
            chartType: ChartType.OS,
            defaultDate: moment().format('YYYY-MM-DD'),
          },
        },
      },
      De: {
        category: SurveyCategory.End,
        label: '후유증 증상',
        desc: ['뇌혈관질환 후유증 증상을 모두 선택하세요.'],
        selectType: SelectType.Check,
        chartType: ChartType.CC,
        answers: [
          { label: '팔다리 마비', value: 1, symptomCode: ['S045'] },
          { label: '감각이 둔해요.', value: 2, symptomCode: ['S000'] },
          { label: '감각이 과민해요(통증)', value: 3, symptomCode: ['S000'] },
          { label: '삼킴 곤란(혹은 콧줄)', value: 4, symptomCode: ['S000'] },
          { label: '배뇨 곤란(혹은 소변줄)', value: 5, symptomCode: ['S000'] },
          { label: '호흡 곤란(혹은 목관)', value: 6, symptomCode: ['S000'] },
          { label: '변비', value: 7, symptomCode: ['S126'] },
          { label: '소화불량', value: 8, symptomCode: ['S178'] },
          { label: '설사', value: 9, symptomCode: ['S159'] },
          { label: '우울증', value: 10, symptomCode: ['S227'] },
          { label: '섬망', value: 11, symptomCode: ['S615'] },
          { label: '불면', value: 12, symptomCode: ['S000'] },
          { label: '인지장애', value: 13, symptomCode: ['S000'] },
          { label: '두통', value: 14, symptomCode: ['S087'] },
          { label: '어지럼증', value: 15, symptomCode: ['S325'] },
          { label: '피로', value: 16, symptomCode: ['S056'] },
          { label: '의식저하', value: 17, symptomCode: ['S000'] },
          { label: '발음이 어눌해요.', value: 18, symptomCode: ['S000'] },
          { label: '말을 이해하지 못해요.', value: 19, symptomCode: ['S000'] },
          { label: '말을 하지 못해요.', value: 20, symptomCode: ['S000'] },
          { label: '시야장애', value: 21, symptomCode: ['S000'] },
          { label: '기타(주관식)', value: -1 },
        ],
      },
      Df: {
        category: SurveyCategory.End,
        label: '기저질환',
        desc: ['기저질환을 모두 선택하세요.'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '해당 질환이 없어요.', value: 0 },
          { label: '고혈압', value: 1, symptomCode: ['S023'] },
          { label: '당뇨', value: 2, symptomCode: ['S167'] },
          { label: '고지혈증', value: 3, symptomCode: ['S022'] },
          { label: '심근경색', value: 4, symptomCode: ['S036'] },
          { label: '부정맥', value: 5, symptomCode: ['S134'] },
          { label: '협심증', value: 6, symptomCode: ['S329', 'S036'] },
          { label: '기타(주관식)', value: -1 },
        ],
      },
      Dg: {
        category: SurveyCategory.End,
        label: '복용약',
        desc: ['복용 중인 약물을 모두 선택하세요.'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '복용 중인 약이 없어요.', value: 0 },
          { label: '고혈압약', value: 1, symptomCode: ['S000'] },
          { label: '당뇨약', value: 2, symptomCode: ['S000'] },
          { label: '고지혈증약', value: 3, symptomCode: ['S000'] },
          { label: '항혈전제(아스피린 등)', value: 4, symptomCode: ['S000'] },
          { label: '항우울제', value: 5, symptomCode: ['S000'] },
          { label: '기타(주관식)', value: -1 },
        ],
      },
    },
  },
  E: {
    category: SurveyCategory.Main,
    label: '요추추간판탈출증',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      {
        label: '네, 있어요.',
        next: ['Ea', 'Eb', 'Ec', 'Ed', 'Ee', 'Ef'],
        value: 1,
        symptomCode: ['S766'],
      },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      Ea: {
        category: SurveyCategory.Middle,
        label: '요하지 증상',
        desc: ['요추추간판탈출증 관련하여', '불편한 증상을 모두 고르세요.'],
        selectType: SelectType.Check,
        chartType: ChartType.CC,
        answers: [
          { label: '허리가 쑤시고 아파요.', value: 'Eaa', next: 'Eaa', symptomCode: ['S226'] },
          { label: '허리가 시리고 시큰거려요.', value: 'Eab', next: 'Eab', symptomCode: ['S224'] },
          { label: '다리가 저려요.', value: 'Eac', next: 'Eac', symptomCode: ['S094'] },
          { label: '다리가 당겨요.', value: 'Ead', next: 'Ead', symptomCode: ['S000'] },
          { label: '다리가 욱신거려요.', value: 'Eae', next: 'Eae', symptomCode: ['S309'] },
          { label: '다리가 따끔따끔거려요.', value: 'Eaf', next: 'Eaf', symptomCode: ['S310'] },
          { label: '다리에 힘이 없어요.', value: 'Eag', next: 'Eag', symptomCode: ['S320'] },
          { label: '다리 피부의 감각이 둔해요.', value: 'Eah', next: 'Eah', symptomCode: ['S319'] },
          { label: '회음부 감각이 둔해요.', value: 'Eai', next: 'Eai', symptomCode: ['S319'] },
          { label: '대소변 실금이 있어요.', value: 'Eaj', next: 'Eaj', symptomCode: ['S000'] },
          { label: '대소변이 잘 안 나와요.', value: 'Eak', next: 'Eak', symptomCode: ['S000'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          Eaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Eab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Eac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Ead: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Eae: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Eaf: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Eag: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Eah: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Eai: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Eaj: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Eak: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      Eb: {
        category: SurveyCategory.End,
        label: '증상 악화 요인',
        desc: ['언제 증상이 악화되나요?'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '특별히 없어요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '기침이나 재채기할 때', value: 1, symptomCode: ['S000'] },
          { label: '허리 굽힐 때', value: 2, symptomCode: ['S000'] },
          { label: '허리 펼 때', value: 3, symptomCode: ['S000'] },
          { label: '걸을 때', value: 4, symptomCode: ['S000'] },
          { label: '앉아있을 때', value: 5, symptomCode: ['S000'] },
          { label: '서있을 때', value: 6, symptomCode: ['S000'] },
          { label: '기타(주관식)', value: -1 },
        ],
      },
      Ec: {
        category: SurveyCategory.End,
        label: '최근 증상발현일',
        desc: ['최근 요추추간판탈출증 증상이 나타난 날은 언제인가요?'],
        selectType: SelectType.Date,
        chartType: ChartType.OS,
        defaultDate: moment().format('YYYY-MM-DD'),
      },
      Ed: {
        category: SurveyCategory.Middle,
        label: '치료 내역',
        desc: ['요추추간판탈출증 증상으로', '병/의원을 방문한 적이 있나요?'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '치료받은 적 없어요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '병/의원에서 치료 받았어요.', value: 'Eda', next: 'Eda', symptomCode: ['S000'] },
          { label: '한방병/의원에서 치료 받았어요.', value: 2, symptomCode: ['S000'] },
        ],
        children: {
          Eda: {
            category: SurveyCategory.End,
            label: '치료 내역',
            desc: ['어떤 치료를 받으셨나요?'],
            selectType: SelectType.Check,
            chartType: ChartType.PI,
            answers: [
              { label: '수술', value: 1, symptomCode: ['S000'] },
              { label: '시술', value: 2, symptomCode: ['S000'] },
              { label: '물리 치료', value: 3, symptomCode: ['S000'] },
              { label: '주사 치료', value: 4, symptomCode: ['S000'] },
              { label: '먹는 약 처방', value: 5, symptomCode: ['S000'] },
              { label: '기타(주관식)', value: -1 },
            ],
          },
        },
      },
      Ee: {
        category: SurveyCategory.End,
        label: '검사 내역',
        desc: ['어떤 검사를 받으셨나요?'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '검사 받은 적 없어요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '엑스레이', value: 1, symptomCode: ['S000'] },
          { label: 'CT', value: 2, symptomCode: ['S000'] },
          { label: 'MRI', value: 3, symptomCode: ['S000'] },
          { label: '근전도 검사', value: 4, symptomCode: ['S000'] },
          { label: '기타(주관식)', value: -1 },
        ],
      },
      Ef: {
        category: SurveyCategory.Middle,
        label: '근골격계 질환',
        desc: ['진단 받은 근골격계 질환이 있으신가요?'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '진단받은 근골격계 질환이 없어요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '허리 염좌', value: 'Efa', next: 'Efa', symptomCode: ['S301'] },
          { label: '경추추간판탈출증(목디스크)', value: 'Efb', next: 'Efb', symptomCode: ['S765'] },
          { label: '척추관 협착증', value: 'Efc', next: 'Efc', symptomCode: ['S767'] },
          { label: '척추전방전위증', value: 'Efd', next: 'Efd', symptomCode: ['S000'] },
          { label: '강직성 척추염', value: 'Efe', next: 'Efe', symptomCode: ['S768'] },
          { label: '골다공증', value: 'Eff', next: 'Eff', symptomCode: ['S028'] },
          { label: '척추측만증', value: 'Efg', next: 'Efg', symptomCode: ['S000'] },
          { label: '류마티스 관절염', value: 'Efh', next: 'Efh', symptomCode: ['S032'] },
          { label: '퇴행성 관절염', value: 'Efi', next: 'Efi', symptomCode: ['S764'] },
          { label: '척추 골절', value: 'Efj', next: 'Efj', symptomCode: ['S027'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          Efa: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Efb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Efc: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Efd: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Efe: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Eff: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Efg: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Efh: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Efi: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Efj: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
  F: {
    category: SurveyCategory.Main,
    label: '알레르기 비염',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      {
        label: '네, 있어요.',
        next: ['Fa', 'Fb', 'Fc', 'Fd', 'Fe', 'Ff', 'Fg'],
        value: 1,
        symptomCode: ['S145'],
      },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      Fa: {
        category: SurveyCategory.Middle,
        label: '비염 증상',
        desc: ['알레르기 비염 관련하여', '불편한 증상을 모두 고르세요.'],
        selectType: SelectType.Check,
        chartType: ChartType.CC,
        answers: [
          { label: '맑은 콧물', value: 'Faa', next: 'Faa', symptomCode: ['S300'] },
          { label: '끈적이는 누런 콧물', value: 'Fab', next: 'Fab', symptomCode: ['S300'] },
          { label: '재채기', value: 'Fac', next: 'Fac', symptomCode: ['S000'] },
          { label: '코막힘', value: 'Fad', next: 'Fad', symptomCode: ['S298'] },
          { label: '눈이나 코가 가려움', value: 'Fae', next: 'Fae', symptomCode: ['S177'] },
          { label: '후각감퇴', value: 'Faf', next: 'Faf', symptomCode: ['S000'] },
          { label: '코 통증', value: 'Fag', next: 'Fag', symptomCode: ['S000'] },
          { label: '두통', value: 'Fah', next: 'Fah', symptomCode: ['S087'] },
          { label: '청력장애', value: 'Fai', next: 'Fai', symptomCode: ['S249'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          Faa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Fab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Fac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Fad: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Fae: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Faf: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Fag: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Fah: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Fai: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      Fb: {
        category: SurveyCategory.End,
        label: '최근 증상발현일',
        desc: ['최근 알레르기 비염 증상이 나타난 날은 언제인가요?'],
        selectType: SelectType.Date,
        chartType: ChartType.OS,
        defaultDate: moment().format('YYYY-MM-DD'),
      },
      Fc: {
        category: SurveyCategory.End,
        label: '증상 정도',
        desc: ['증상 정도와 관련해서', '해당되는 사항을 모두 선택하세요.'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '일주일에 4일 이상', value: 1, symptomCode: ['S000'] },
          { label: '일주일에 4일 미만', value: 2, symptomCode: ['S000'] },
          { label: '계절에 상관 있음', value: 3, symptomCode: ['S000'] },
          { label: '계절에 상관 없음', value: 4, symptomCode: ['S000'] },
          { label: '일이나 수면 등 일상 생활 지장이 있음', value: 5, symptomCode: ['S000'] },
          { label: '일이나 수면 등 일상 생활 지장이 없음', value: 6, symptomCode: ['S000'] },
        ],
      },
      Fd: {
        category: SurveyCategory.Middle,
        label: '치료 내역',
        desc: ['알레르기 비염 증상으로', '병/의원을 방문한 적이 있나요?'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '치료받은 적 없어요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '병/의원에서 치료 받았어요.', value: 1, next: 'Fda', symptomCode: ['S000'] },
          { label: '한방병/의원에서 치료 받았어요.', value: 2, symptomCode: ['S000'] },
        ],
        children: {
          Fda: {
            category: SurveyCategory.End,
            label: '치료 내역',
            desc: ['어떤 치료를 받으셨나요?'],
            selectType: SelectType.Check,
            chartType: ChartType.PI,
            answers: [
              { label: '수술', value: 1, symptomCode: ['S000'] },
              { label: '시술', value: 2, symptomCode: ['S000'] },
              { label: '주사 치료', value: 3, symptomCode: ['S000'] },
              { label: '약 처방', value: 4, symptomCode: ['S000'] },
              { label: '기타(주관식)', value: -1 },
            ],
          },
        },
      },
      Fe: {
        category: SurveyCategory.End,
        label: '약물 치료',
        desc: ['알레르기 비염과 관련해서', '사용 중인 약을 모두 선택하세요.'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '관련해서 사용 중인 약이 없어요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '항히스타민제', value: 1, symptomCode: ['S000'] },
          { label: '먹는 스테로이드제', value: 2, symptomCode: ['S000'] },
          { label: '뿌리는 스테로이드제', value: 3, symptomCode: ['S000'] },
          { label: '먹는 비점막수축제', value: 4, symptomCode: ['S000'] },
          { label: '뿌리는 비점막수축제', value: 5, symptomCode: ['S000'] },
          { label: '항류코트리엔제', value: 6, symptomCode: ['S000'] },
          { label: '기타(주관식)', value: -1 },
        ],
      },
      Ff: {
        category: SurveyCategory.Middle,
        label: '관련 질환',
        desc: ['다음 중 진단받은 질환이 있으신가요?'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '진단받은 관련 질환이 없어요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '비부비동염', value: 'Ffa', next: 'Ffa', symptomCode: ['S300', 'S298'] },
          { label: '코 속에 용종', value: 'Ffb', next: 'Ffb', symptomCode: ['S144'] },
          { label: '아토피피부염', value: 'Ffc', next: 'Ffc', symptomCode: ['S189', 'S190', 'S050'] },
          { label: '알레르기성 결막염', value: 'Ffd', next: 'Ffd', symptomCode: ['S109'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          Ffa: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Ffb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Ffc: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Ffd: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
      Fg: {
        category: SurveyCategory.End,
        label: '알레르기 가족력',
        desc: [
          '부모형제 중에 알레르기 비염, 아토피피부염,',
          '알레르기성 결막염 등의',
          '알레르기 질환이 있는 분이 있나요?',
        ],
        selectType: SelectType.Radio,
        chartType: ChartType.FH,
        answers: [
          { label: '있어요.', value: 1, symptomCode: ['S000'] },
          { label: '없어요.', value: 0, mustShowFlagInYejinSymptomTable: true, symptomCode: ['S000'] },
        ],
      },
    },
  },
  G: {
    category: SurveyCategory.Main,
    label: '기능성 소화불량',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      {
        label: '네, 있어요.',
        next: ['Ga', 'Gb', 'Gc', 'Gd', 'Ge', 'Gf', 'Gg'],
        value: 1,
        symptomCode: ['S178'],
      },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      Ga: {
        category: SurveyCategory.Middle,
        label: '소화불량 증상',
        desc: ['소화불량 관련하여 불편한 증상을 모두 고르세요.'],
        selectType: SelectType.Check,
        chartType: ChartType.CC,
        answers: [
          { label: '윗배가 갑갑해요.', value: 'Gaa', next: 'Gaa', symptomCode: ['S219'] },
          { label: '배에 가스가 차고 빵빵해요.', value: 'Gab', next: 'Gab', symptomCode: ['S143'] },
          { label: '속이 더부룩해요.', value: 'Gac', next: 'Gac', symptomCode: ['S194'] },
          { label: '현재 체해서 속이 답답해요.', value: 'Gad', next: 'Gad', symptomCode: ['S196'] },
          {
            label: '식사량이 적고 조금만 먹어도 쉽게 배가 불러요.',
            value: 'Gae',
            next: 'Gae',
            symptomCode: ['S000'],
          },
          { label: '입맛이 없어요.', value: 'Gaf', next: 'Gaf', symptomCode: ['S192'] },
          { label: '속이 메스껍고 구역질이 나요.', value: 'Gag', next: 'Gag', symptomCode: ['S040'] },
          { label: '신물이 자주 올라와요.', value: 'Gah', next: 'Gah', symptomCode: ['S302'] },
          { label: '구토를 자주 해요.', value: 'Gai', next: 'Gai', symptomCode: ['S044'] },
          { label: '딸꾹질을 자주 해요.', value: 'Gaj', next: 'Gaj', symptomCode: ['S088'] },
          { label: '트림을 자주 해요.', value: 'Gak', next: 'Gak', symptomCode: ['S310'] },
          { label: '명치 밑(윗배)이 아파요.', value: 'Gal', next: 'Gal', symptomCode: ['S218'] },
          { label: '아랫배(배꼽 아래)가 아파요.', value: 'Gam', next: 'Gam', symptomCode: ['S131'] },
          { label: '윗배(배꼽 위)가 아파요.', value: 'Gan', next: 'Gan', symptomCode: ['S218'] },
          { label: '옆구리가 결리고 아파요.', value: 'Gao', next: 'Gao', symptomCode: ['S330'] },
          { label: '속이 쓰려요.', value: 'Gap', next: 'Gap', symptomCode: ['S180'] },
          { label: '배에서 꾸르륵 소리가 나요.', value: 'Gaq', next: 'Gaq', symptomCode: ['S265'] },
          { label: '입이 써요.', value: 'Gar', next: 'Gar', symptomCode: ['S037'] },
          { label: '입이 바짝바짝 말라요.', value: 'Gas', next: 'Gas', symptomCode: ['S041'] },
          { label: '갈증이 나서 물을 많이 마셔요.', value: 'Gat', next: 'Gat', symptomCode: ['S006'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          Gaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gad: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gae: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gaf: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gag: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gah: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gai: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gaj: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gak: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gal: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gam: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gan: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gao: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gap: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gaq: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gar: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gas: { category: SurveyCategory.End, selectType: SelectType.Scale },
          Gat: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      Gb: {
        category: SurveyCategory.End,
        label: '소화불량 발병일',
        desc: ['소화불량 증상이 처음 발생한 시점을 알려주세요.'],
        selectType: SelectType.Radio,
        chartType: ChartType.OS,
        answers: [
          { label: '3개월 이내', value: 1, symptomCode: ['S000'] },
          { label: '6개월 이내', value: 2, symptomCode: ['S000'] },
          { label: '6개월 이상', value: 3, symptomCode: ['S000'] },
        ],
      },
      Gc: {
        category: SurveyCategory.End,
        label: '최근 증상발현일',
        desc: ['최근 소화불량 증상이 나타난 날은 언제인가요?'],
        selectType: SelectType.Date,
        chartType: ChartType.OS,
        defaultDate: moment().format('YYYY-MM-DD'),
      },
      Gd: {
        category: SurveyCategory.End,
        label: '증상 빈도',
        desc: ['소화불량 증상은 얼마나 자주 있으신가요?'],
        selectType: SelectType.Radio,
        chartType: ChartType.PI,
        answers: [
          { label: '가끔', value: 1, symptomCode: ['S000'] },
          { label: '일주일에 1일 이상', value: 2, symptomCode: ['S000'] },
          { label: '일주일에 3일 이상', value: 3, symptomCode: ['S000'] },
          { label: '거의 매일', value: 4, symptomCode: ['S000'] },
        ],
      },
      Ge: {
        category: SurveyCategory.Middle,
        label: '치료 내역',
        desc: ['소화불량 증상으로', '병/의원을 방문한 적이 있나요?'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '치료받은 적 없어요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '병/의원에서 치료 받았어요.', value: 'Gea', next: 'Gea', symptomCode: ['S000'] },
          { label: '한방병/의원에서 치료 받았어요.', value: 2, symptomCode: ['S000'] },
        ],
        children: {
          Gea: {
            category: SurveyCategory.End,
            label: '치료 내역',
            desc: ['어떤 치료를 받으셨나요?'],
            selectType: SelectType.Check,
            chartType: ChartType.PI,
            answers: [
              { label: '수술', value: 1, symptomCode: ['S000'] },
              { label: '시술', value: 2, symptomCode: ['S000'] },
              { label: '먹는 약 처방', value: 3, symptomCode: ['S000'] },
              { label: '기타(주관식)', value: -1 },
            ],
          },
        },
      },
      Gf: {
        category: SurveyCategory.End,
        label: '검사 내역',
        desc: ['어떤 검사를 받으셨나요?'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '검사 받은 적 없어요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '위내시경', value: 1, symptomCode: ['S000'] },
          { label: '복부초음파', value: 2, symptomCode: ['S000'] },
          { label: '위전도', value: 3, symptomCode: ['S000'] },
          { label: '복부 CT', value: 4, symptomCode: ['S000'] },
          { label: '복부 MRI', value: 5, symptomCode: ['S000'] },
          { label: '헬리코박터균 검사', value: 6, symptomCode: ['S000'] },
          { label: '기타(주관식)', value: -1 },
        ],
      },
      Gg: {
        category: SurveyCategory.Middle,
        label: '소화기 질환',
        desc: ['진단 받은 소화기 질환이 있으신가요?'],
        selectType: SelectType.Check,
        chartType: ChartType.PI,
        answers: [
          { label: '진단받은 소화기 질환이 없어요.', value: 0, mustShowFlagInYejinSymptomTable: true },
          { label: '식도 악성종양', value: 'Gga', next: 'Gga', symptomCode: ['S000'] },
          { label: '위 악성종양', value: 'Ggb', next: 'Ggb', symptomCode: ['S000'] },
          { label: '십이지장 악성종양', value: 'Ggc', next: 'Ggc', symptomCode: ['S000'] },
          { label: '소화성궤양', value: 'Ggd', next: 'Ggd', symptomCode: ['S179'] },
          { label: '역류성 식도염', value: 'Gge', next: 'Gge', symptomCode: ['S726'] },
          { label: '위염', value: 'Ggf', next: 'Ggf', symptomCode: ['S727'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          Gga: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Ggb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Ggc: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Ggd: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Gge: { category: SurveyCategory.End, selectType: SelectType.Onset },
          Ggf: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
};

export const surveyObj: ISurvey = {
  a: {
    category: SurveyCategory.Main,
    label: '소화',
    desc: ['소화 시 문제가 있으신가요?', '(소화가 잘되지 않음,', '식욕, 갈증, 구역·구토에 문제가 있음)'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['aa', 'ab', 'ac', 'ad'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      aa: {
        category: SurveyCategory.Middle,
        label: '소화',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '최근에 소화가 잘 안돼요.', next: 'aaa', value: 'aaa', symptomCode: ['S178'] },
          {
            label: '늘 소화가 안되고 속이 더부룩해요.',
            next: 'aab',
            value: 'aab',
            symptomCode: ['S194'],
          },
          { label: '현재 체해서 속이 답답해요.', next: 'aac', value: 'aac', symptomCode: ['S196'] },
        ],
        children: {
          aaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          aab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          aac: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      ab: {
        category: SurveyCategory.Middle,
        label: '식욕',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '요즘 입맛이 없어요.', next: 'aba', value: 'aba', symptomCode: ['S192'] },
          { label: '밥을 먹은 후에도 배가 금방 고파요.', next: 'abb', value: 'abb', symptomCode: ['S168'] },
        ],
        children: {
          aba: { category: SurveyCategory.End, selectType: SelectType.Scale },
          abb: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      ac: {
        category: SurveyCategory.Middle,
        label: '갈증',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          {
            label: '갈증이 나서 물을 많이 마셔요.',
            next: ['aca', 'acb'],
            value: 'aca',
            symptomCode: ['S006'],
          },
        ],
        children: {
          aca: { category: SurveyCategory.End, selectType: SelectType.Scale },
          acb: {
            category: SurveyCategory.End,
            label: '물 선호',
            desc: ['갈증이 날 때 어떤 물을 선호하시나요?'],
            selectType: SelectType.Radio,
            answers: [
              { label: '찬물이 좋아요.', value: 1, symptomCode: ['S716'] },
              { label: '따뜻한 물이 좋아요.', value: 2, symptomCode: ['S717'] },
              { label: '찬물이든 더운물이든 상관없어요.', value: 3, symptomCode: ['S718'] },
            ],
          },
        },
      },
      ad: {
        category: SurveyCategory.Middle,
        label: '구역/구토',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '자주 메스껍고 구역질이 나요.', next: 'ada', value: 'ada', symptomCode: ['S040'] },
          {
            label: '구토(먹은 음식물을 토함)를 자주 해요.',
            next: 'adb',
            value: 'adb',
            symptomCode: ['S044'],
          },
          { label: '토혈(피가 섞인 토)을 해요.', next: 'adc', value: 'adc', symptomCode: ['S307'] },
          { label: '딸꾹질을 자주 해요.', next: 'add', value: 'add', symptomCode: ['S088'] },
          { label: '트림을 자주 해요.', next: 'ade', value: 'ade', symptomCode: ['S310'] },
          { label: '신물이 자주 올라와요.', next: 'adf', value: 'adf', symptomCode: ['S302'] },
          { label: '속이 쓰려요.', next: 'adg', value: 'adg', symptomCode: ['S180'] },
        ],
        children: {
          ada: { category: SurveyCategory.End, selectType: SelectType.Scale },
          adb: { category: SurveyCategory.End, selectType: SelectType.Scale },
          adc: { category: SurveyCategory.End, selectType: SelectType.Scale },
          add: { category: SurveyCategory.End, selectType: SelectType.Scale },
          ade: { category: SurveyCategory.End, selectType: SelectType.Scale },
          adf: { category: SurveyCategory.End, selectType: SelectType.Scale },
          adg: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
    },
  },
  b: {
    category: SurveyCategory.Main,
    label: '소변',
    desc: ['소변 시 문제가 있으신가요?', '(소변이 너무 잦음·적음, 야뇨증 등)'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['ba', 'bb', 'bc', 'bd'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      ba: {
        category: SurveyCategory.Middle,
        label: '소변 빈도',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          {
            label: '소변이 잘 안 나오거나 방울방울 나와요.',
            next: 'baa',
            value: 'baa',
            symptomCode: ['S242'],
          },
          {
            label: '소변을 자주 봐요.(하루 7회 이상)',
            next: 'bab',
            value: 'bab',
            symptomCode: ['S147'],
          },
          {
            label: '평소보다 소변 양이 많은 것 같아요.',
            next: 'bac',
            value: 'bac',
            symptomCode: ['S067'],
          },
          { label: '밤 중에 자다 깨서 소변을 봐요.', next: 'bad', value: 'bad', symptomCode: ['S202'] },
        ],
        children: {
          baa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          bab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          bac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          bad: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      bb: {
        category: SurveyCategory.Middle,
        label: '배뇨 시 증상',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '소변을 볼 때 통증이 있어요. ', next: 'bba', value: 'bba', symptomCode: ['S063'] },
          { label: '소변 양이 적고 시원하지 않아요.', next: 'bbb', value: 'bbb', symptomCode: ['S169'] },
          {
            label: '소변을 볼 때 저린감, 열감이 있어요.',
            next: 'bbc',
            value: 'bbc',
            symptomCode: ['S117'],
          },
          {
            label: '소변을 보고 난 후에도 개운하지 않아요.',
            next: 'bbd',
            value: 'bbd',
            symptomCode: ['S263'],
          },
          {
            label: '소변을 잘 참기가 힘들어요.',
            next: 'bbe',
            value: 'bbe',
            symptomCode: ['S274'],
          },
          { label: '요실금이 있어요.', next: 'bbf', value: 'bbf', symptomCode: ['S225'] },
        ],
        children: {
          bba: { category: SurveyCategory.End, selectType: SelectType.Scale },
          bbb: { category: SurveyCategory.End, selectType: SelectType.Scale },
          bbc: { category: SurveyCategory.End, selectType: SelectType.Scale },
          bbd: { category: SurveyCategory.End, selectType: SelectType.Scale },
          bbe: { category: SurveyCategory.End, selectType: SelectType.Scale },
          bbf: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      bc: {
        category: SurveyCategory.Middle,
        label: '소변 색',
        selectType: SelectType.Radio,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '소변에 피가 섞여 있어요. ', next: 'bca', value: 'bca', symptomCode: ['S066'] },
          { label: '소변색이 붉어요.', next: 'bcb', value: 'bcb', symptomCode: ['S064'] },
          { label: '소변이 뿌예요.', next: 'bcc', value: 'bcc', symptomCode: ['S065'] },
          { label: '소변이 검어요.', next: 'bcd', value: 'bcd', symptomCode: ['S733'] },
        ],
        children: {
          bca: { category: SurveyCategory.End, selectType: SelectType.Scale },
          bcb: { category: SurveyCategory.End, selectType: SelectType.Scale },
          bcc: { category: SurveyCategory.End, selectType: SelectType.Scale },
          bcd: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      bd: {
        category: SurveyCategory.Middle,
        label: '소변 질환',
        desc: ['현재 앓고 계신 질환을 모두 골라주세요.'],
        selectType: SelectType.Check,
        answers: [
          { label: '앓고 있는 질환이 없어요.', value: 0 },
          { label: '요도염 ', next: 'bda', value: 'bda', symptomCode: ['S222'] },
          { label: '방광염', next: 'bdb', value: 'bdb', symptomCode: ['S116'] },
          { label: '요로결석', next: 'bdc', value: 'bdc', symptomCode: ['S223'] },
          { label: '전립선비대증', next: 'bdd', value: 'bdd', symptomCode: ['S272'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          bda: { category: SurveyCategory.End, selectType: SelectType.Onset },
          bdb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          bdc: { category: SurveyCategory.End, selectType: SelectType.Onset },
          bdd: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
  c: {
    category: SurveyCategory.Main,
    label: '대변',
    desc: ['대변 시 문제가 있으신가요?', '(설사, 변비, 혈변, 치질 등)'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['ca', 'cb'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      ca: {
        category: SurveyCategory.Middle,
        label: '대변',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '설사 ', next: 'caa', value: 'caa', symptomCode: ['S159'] },
          { label: '변비', next: 'cab', value: 'cab', symptomCode: ['S126'] },
          { label: '대변에 피가 섞여있다.', next: 'cac', value: 'cac', symptomCode: ['S127'] },
        ],
        children: {
          caa: {
            category: SurveyCategory.Middle,
            label: '설사 양상',
            selectType: SelectType.Check,
            answers: [
              { label: '하루 3번 이상 설사를 해요.', next: 'caaa', value: 'caaa', symptomCode: ['S734'] },
              { label: '대변이 묽어요.', next: 'caab', value: 'caab', symptomCode: ['S735'] },
              { label: '물 같은 설사를 해요.', next: 'caac', value: 'caac', symptomCode: ['S736'] },
              {
                label: '대변을 보고난 후에도 시원하지 않아요.',
                next: 'caad',
                value: 'caad',
                symptomCode: ['S737'],
              },
              {
                label: '찬 것을 먹으면 설사를 자주 해요.',
                next: 'caae',
                value: 'caae',
                symptomCode: ['S738'],
              },
              { label: '술 마신 후에는 설사를 해요.', next: 'caaf', value: 'caaf', symptomCode: ['S739'] },
              { label: '기타(주관식)', value: -1 },
            ],
            children: {
              caaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
              caab: { category: SurveyCategory.End, selectType: SelectType.Scale },
              caac: { category: SurveyCategory.End, selectType: SelectType.Scale },
              caad: { category: SurveyCategory.End, selectType: SelectType.Scale },
              caae: { category: SurveyCategory.End, selectType: SelectType.Scale },
              caaf: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
          cab: {
            category: SurveyCategory.Middle,
            label: '변비 양상',
            selectType: SelectType.Check,
            answers: [
              { label: '3일 이상 변을 보지 못했어요.', next: 'caba', value: 'caba', symptomCode: ['S740'] },
              { label: '대변이 딱딱해요.', next: 'cabb', value: 'cabb', symptomCode: ['S741'] },
              {
                label: '대변 보기가 어려워요.',
                next: 'cabc',
                value: 'cabc',
                symptomCode: ['S742'],
              },
              {
                label: '대변 보고 난 후에도 시원하지 않아요.',
                next: 'cabd',
                value: 'cabd',
                symptomCode: ['S743'],
              },
              { label: '기타(주관식)', value: -1 },
            ],
            children: {
              caba: { category: SurveyCategory.End, selectType: SelectType.Scale },
              cabb: { category: SurveyCategory.End, selectType: SelectType.Scale },
              cabc: { category: SurveyCategory.End, selectType: SelectType.Scale },
              cabd: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
          cac: {
            category: SurveyCategory.Middle,
            label: '변혈 양상',
            selectType: SelectType.Check,
            answers: [
              {
                label: '대변이 전체적으로 빨간색이에요.',
                next: 'caca',
                value: 'caca',
                symptomCode: ['S744'],
              },
              {
                label: '대변이 전체적으로 검은색이에요. ',
                next: 'cacb',
                value: 'cacb',
                symptomCode: ['S745'],
              },
              {
                label: '대변 끝에 피가 나오거나 휴지에 묻어요.',
                next: 'cacc',
                value: 'cacc',
                symptomCode: ['S746'],
              },
              { label: '기타(주관식)', value: -1 },
            ],
            children: {
              caca: { category: SurveyCategory.End, selectType: SelectType.Scale },
              cacb: { category: SurveyCategory.End, selectType: SelectType.Scale },
              cacc: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
        },
      },
      cb: {
        category: SurveyCategory.Middle,
        label: '대변 질환',
        desc: ['현재 앓고 계신 질환을 모두 골라주세요.'],
        selectType: SelectType.Check,
        answers: [
          { label: '앓고 있는 질환이 없어요.', value: 0 },
          { label: '과민성대장증후군', next: 'cba', value: 'cba', symptomCode: ['S030'] },
          { label: '치질', next: 'cbb', value: 'cbb', symptomCode: ['S296'] },
          { label: '탈항', next: 'cbc', value: 'cbc', symptomCode: ['S304'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          cba: { category: SurveyCategory.End, selectType: SelectType.Onset },
          cbb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          cbc: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
  d: {
    category: SurveyCategory.Main,
    label: '땀',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['da'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      da: {
        category: SurveyCategory.Middle,
        label: '땀',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          {
            label: '가만히 있어도 땀이 흥건하게 많이 나요.',
            next: ['daa', 'dae'],
            value: 'daa',
            symptomCode: ['S071'],
          },
          {
            label: '다른 사람들보다 땀이 잘 나는 편이에요.',
            next: ['dab'],
            value: 'dab',
            symptomCode: ['S089'],
          },
          {
            label: '운동을 해도 땀이 잘 나지 않아요.',
            next: ['dac'],
            value: 'dac',
            symptomCode: ['S112'],
          },
          {
            label: '식은땀이 나요.',
            next: ['dad'],
            value: 'dad',
            symptomCode: ['S193'],
          },
        ],
        children: {
          daa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          dab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          dac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          dad: { category: SurveyCategory.End, selectType: SelectType.Scale },
          dae: {
            category: SurveyCategory.End,
            label: '발한 부위',
            desc: ['주로 땀이 나는 부위를 골라주세요.'],
            selectType: SelectType.Check,
            answers: [
              { label: '얼굴', value: 1, symptomCode: ['S747'] },
              { label: '목', value: 2, symptomCode: ['S748'] },
              { label: '가슴', value: 3, symptomCode: ['S749'] },
              { label: '등', value: 4, symptomCode: ['S750'] },
              { label: '겨드랑이', value: 5, symptomCode: ['S751'] },
              { label: '손, 발', value: 6, symptomCode: ['S752'] },
              { label: '사타구니', value: 7, symptomCode: ['S753'] },
              { label: '전신', value: 8, symptomCode: ['S754'] },
            ],
          },
        },
      },
    },
  },
  e: {
    category: SurveyCategory.Main,
    label: '한열',
    desc: ['몸의 열감/냉감 또는 더위/추위와 관련하여', '문제가 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['ea', 'eb'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      ea: {
        category: SurveyCategory.Middle,
        label: '더위/추위',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '현재 고열(38도 이상)이 있어요.', next: 'eaa', value: 'eaa', symptomCode: ['S021'] },
          { label: '살짝 열이 있어요.(37~38도)', next: 'eab', value: 'eab', symptomCode: ['S115'] },
          {
            label: '몸에 열감이 느껴져요.',
            next: 'eac',
            value: 'eac',
            symptomCode: ['S323'],
          },
          { label: '오한이 들어요.', next: 'ead', value: 'ead', symptomCode: ['S216'] },
          { label: '더웠다가 추웠다가 왔다갔다 해요.', next: 'eae', value: 'eae', symptomCode: ['S322'] },
          { label: '평소 추위를 많이 타요.', next: 'eaf', value: 'eaf', symptomCode: ['S759'] },
          { label: '평소 더위를 많이 타요.', next: 'eag', value: 'eag', symptomCode: ['S760'] },
        ],
        children: {
          eaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          eab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          eac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          ead: { category: SurveyCategory.End, selectType: SelectType.Scale },
          eae: { category: SurveyCategory.End, selectType: SelectType.Scale },
          eaf: { category: SurveyCategory.End, selectType: SelectType.Scale },
          eag: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      eb: {
        category: SurveyCategory.Middle,
        label: '신체 한열',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '얼굴에 열이 훅 오를 때가 있어요.', next: 'eba', value: 'eba', symptomCode: ['S125'] },
          { label: '손발에 열이 나요.', next: 'ebb', value: 'ebb', symptomCode: ['S215'] },
          { label: '손발이 차요.', next: 'ebc', value: 'ebc', symptomCode: ['S184'] },
          { label: '온몸이 차요.', next: 'ebd', value: 'ebd', symptomCode: ['S197'] },
          { label: '배가 차요.', next: 'ebe', value: 'ebe', symptomCode: ['S128'] },
        ],
        children: {
          eba: { category: SurveyCategory.End, selectType: SelectType.Scale },
          ebb: { category: SurveyCategory.End, selectType: SelectType.Scale },
          ebc: { category: SurveyCategory.End, selectType: SelectType.Scale },
          ebd: { category: SurveyCategory.End, selectType: SelectType.Scale },
          ebe: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
    },
  },
  f: {
    category: SurveyCategory.Main,
    label: '부종',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['fa'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      fa: {
        category: SurveyCategory.Middle,
        label: '부종',
        desc: ['평소에 잘 붓는 부위가 있으신가요?'],
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '전신', next: ['faa', 'fax'], value: 'faa', symptomCode: ['S135'] },
          { label: '얼굴', next: ['fab', 'fax'], value: 'fab', symptomCode: ['S084'] },
          { label: '눈/눈꺼풀', next: ['fac', 'fax'], value: 'fac', symptomCode: ['S106'] },
          {
            label: '피부',
            next: ['fad', 'fax'],
            value: 'fad',
            symptomCode: ['S318'],
          },
          { label: '다리', next: ['fae', 'fax'], value: 'fae', symptomCode: ['S321'] },
          { label: '손', next: ['fah', 'fax'], value: 'fah', symptomCode: ['S796'] },
          { label: '발', next: ['fai', 'fax'], value: 'fai', symptomCode: ['S797'] },
        ],
        children: {
          faa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          fab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          fac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          fad: { category: SurveyCategory.End, selectType: SelectType.Scale },
          fae: { category: SurveyCategory.End, selectType: SelectType.Scale },
          fah: { category: SurveyCategory.End, selectType: SelectType.Scale },
          fai: { category: SurveyCategory.End, selectType: SelectType.Scale },
          fax: {
            // 손/발 추가로 순서때문에 새로 부여된 코드
            category: SurveyCategory.End,
            label: '붓는 시간',
            desc: ['주로 언제 붓나요?'],
            selectType: SelectType.Radio,
            answers: [
              { label: '아침(기상 후)', value: 1, symptomCode: ['S761'] },
              { label: '저녁(활동 후)', value: 2, symptomCode: ['S762'] },
              { label: '종일', value: 3, symptomCode: ['S763'] },
            ],
          },
          fag: {
            deprecated: true,
            category: SurveyCategory.End,
            label: '붓는 시간',
            desc: ['주로 언제 붓나요?'],
            selectType: SelectType.Radio,
            answers: [
              { label: '아침(기상 후)', value: 1, symptomCode: ['S761'] },
              { label: '저녁(활동 후)', value: 2, symptomCode: ['S762'] },
              { label: '종일', value: 3, symptomCode: ['S763'] },
            ],
          },
        },
      },
    },
  },
  g: {
    category: SurveyCategory.Main,
    label: '감정/정신',
    desc: ['마음에 힘든 일이 있으신가요?', '(요즘 우울합니다, 화가 납니다,', '스트레스를 많이 받습니다)'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['ga', 'gb'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      ga: {
        category: SurveyCategory.Middle,
        label: '감정/정신 질환',
        desc: ['진단받거나 앓고 있는 질환이 있으신가요?'],
        selectType: SelectType.Check,
        answers: [
          { label: '앓고 있는 질환이 없어요.', value: 0 },
          { label: '조울증', next: 'gaa', value: 'gaa', symptomCode: ['S776'] },
          { label: '우울증', next: 'gab', value: 'gab', symptomCode: ['S777'] },
          { label: '강박장애', next: 'gac', value: 'gac', symptomCode: ['S778'] },
          { label: '공황장애', next: 'gad', value: 'gad', symptomCode: ['S779'] },
          { label: '범불안장애', next: 'gae', value: 'gae', symptomCode: ['S780'] },
          { label: '히스테리', next: 'gaf', value: 'gaf', symptomCode: ['S133'] },
          { label: '신경 쇠약', next: 'gag', value: 'gag', symptomCode: ['S276'] },
          { label: '조현병', next: 'gah', value: 'gah', symptomCode: ['S035'] },
          { label: '뇌전증', next: 'gai', value: 'gai', symptomCode: ['S270'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          gaa: { category: SurveyCategory.End, selectType: SelectType.Onset },
          gab: { category: SurveyCategory.End, selectType: SelectType.Onset },
          gac: { category: SurveyCategory.End, selectType: SelectType.Onset },
          gad: { category: SurveyCategory.End, selectType: SelectType.Onset },
          gae: { category: SurveyCategory.End, selectType: SelectType.Onset },
          gaf: { category: SurveyCategory.End, selectType: SelectType.Onset },
          gag: { category: SurveyCategory.End, selectType: SelectType.Onset },
          gah: { category: SurveyCategory.End, selectType: SelectType.Onset },
          gai: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
      gb: {
        category: SurveyCategory.Middle,
        label: '감정/정신',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '자주 심장이 두근거려요.', next: 'gba', value: 'gba', symptomCode: ['S199'] },
          { label: '잘 놀라요.', next: 'gbb', value: 'gbb', symptomCode: ['S015'] },
          { label: '화가 잘 나요.', next: 'gbc', value: 'gbc', symptomCode: ['S136'] },
          { label: '불안감을 자주 느껴요.', next: 'gbd', value: 'gbd', symptomCode: ['S139'] },
          {
            label: '자주 쉽게 우울해진다.',
            next: 'gbe',
            value: 'gbe',
            symptomCode: ['S227'],
          },
          { label: '잘 잊어버린다.', next: 'gbf', value: 'gbf', symptomCode: ['S010'] },
          {
            label: '정신이 무력하고 의욕이 없다.',
            next: 'gbg',
            value: 'gbg',
            symptomCode: ['S276'],
          },
        ],
        children: {
          gba: { category: SurveyCategory.End, selectType: SelectType.Scale },
          gbb: { category: SurveyCategory.End, selectType: SelectType.Scale },
          gbc: { category: SurveyCategory.End, selectType: SelectType.Scale },
          gbd: { category: SurveyCategory.End, selectType: SelectType.Scale },
          gbe: { category: SurveyCategory.End, selectType: SelectType.Scale },
          gbf: { category: SurveyCategory.End, selectType: SelectType.Scale },
          gbg: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
    },
  },
  h: {
    category: SurveyCategory.Main,
    label: '수면',
    desc: [
      '수면 시 문제가 있으신가요?',
      '(잠을 못 자서 피곤합니다,',
      '잠에 잘 들지 못합니다, 중간에 깹니다)',
    ],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['ha'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      ha: {
        category: SurveyCategory.Middle,
        label: '수면',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          {
            label: '잠을 잘 못자요.',
            next: 'haa',
            value: 'haa',
            symptomCode: ['S138'],
          },
          { label: '꿈을 많이 꿔요.', next: 'hab', value: 'hab', symptomCode: ['S069'] },
          { label: '자고 일어나도 피곤해요.', next: 'hac', value: 'hac', symptomCode: ['S273'] },
          {
            label: '잠에 잘 들지 못해요.',
            next: 'had',
            value: 'had',
            symptomCode: ['S781'],
          },
          { label: '잠을 자다가 중간에 잘 깨요.', next: 'hae', value: 'hae', symptomCode: ['S782'] },
          { label: '더 자고 싶은데 이른 시간에 깨요.', next: 'haf', value: 'haf', symptomCode: ['S783'] },
        ],
        children: {
          haa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          hab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          hac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          had: { category: SurveyCategory.End, selectType: SelectType.Scale },
          hae: { category: SurveyCategory.End, selectType: SelectType.Scale },
          haf: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
    },
  },
  i: {
    category: SurveyCategory.Main,
    label: '남성',
    desc: ['관련된 증상을 모두 선택하세요.'],
    selectType: SelectType.Check,
    answers: [
      { label: '해당 증상이 없어요.', value: 0 },
      { label: '조루', next: 'ia', value: 'ia', symptomCode: ['S279'] },
      { label: '정액이 새어나와요.', next: 'ib', value: 'ib', symptomCode: ['S239'] },
      { label: '발기부전', next: 'ic', value: 'ic', symptomCode: ['S204', 'S275'] },
      { label: '발기지속', next: 'id', value: 'id', symptomCode: ['S284'] },
      { label: '고환이 붓거나 통증이 있어요.', next: 'ie', value: 'ie', symptomCode: ['S024'] },
      { label: '생식기와 아랫배가 당기고 아파요.', next: 'if', value: 'if', symptomCode: ['S150'] },
      { label: '사타구니가 자주 가려워요.', next: 'ig', value: 'ig', symptomCode: ['S243'] },
    ],
    children: {
      ia: { category: SurveyCategory.End, selectType: SelectType.Scale },
      ib: { category: SurveyCategory.End, selectType: SelectType.Scale },
      ic: { category: SurveyCategory.End, selectType: SelectType.Scale },
      id: { category: SurveyCategory.End, selectType: SelectType.Scale },
      ie: { category: SurveyCategory.End, selectType: SelectType.Scale },
      if: { category: SurveyCategory.End, selectType: SelectType.Scale },
      ig: { category: SurveyCategory.End, selectType: SelectType.Scale },
    },
  },
  j: {
    category: SurveyCategory.Main,
    label: '임신/출산',
    desc: ['임신 중이시거나', '혹은 임신/출산 경험이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '예, 임신/출산 경험이 있습니다.', next: ['ja', 'jb', 'jc', 'jd'], value: 1 },
      { label: '아니요, 임신/출산 경험이 없습니다.', value: 0 },
    ],
    children: {
      ja: {
        category: SurveyCategory.Middle,
        label: '산과력',
        desc: ['출산 경험이 있으신가요?'],
        selectType: SelectType.Radio,
        answers: [
          { label: '출산 경험 0회', value: 0, symptomCode: ['S784'] },
          { label: '출산 경험 1회', value: 1, symptomCode: ['S785'] },
          { label: '출산 경험 2회', value: 2, symptomCode: ['S786'] },
          { label: '출산 경험 3회 이상', value: 3, symptomCode: ['S787'] },
        ],
      },
      jb: {
        category: SurveyCategory.Middle,
        label: '산과력',
        desc: ['조산(37주 미만) 경험이 있으신가요?'],
        selectType: SelectType.Radio,
        answers: [
          { label: '조산 경험 0회', value: 0, symptomCode: ['S788'] },
          { label: '조산 경험 1회', value: 1, symptomCode: ['S789'] },
          { label: '조산 경험 2회', value: 2, symptomCode: ['S790'] },
          { label: '조산 경험 3회 이상', value: 3, symptomCode: ['S791'] },
        ],
      },
      jc: {
        category: SurveyCategory.Middle,
        label: '산과력',
        desc: ['유산 경험이 있으신가요?'],
        selectType: SelectType.Radio,
        answers: [
          { label: '유산 경험 0회', value: 0, symptomCode: ['S792'] },
          { label: '유산 경험 1회', value: 1, symptomCode: ['S793'] },
          { label: '유산 경험 2회', value: 2, symptomCode: ['S794'] },
          { label: '유산 경험 3회 이상', value: 3, symptomCode: ['S795'] },
        ],
      },
      jd: {
        category: SurveyCategory.Middle,
        label: '임신/출산',
        selectType: SelectType.Radio,
        answers: [
          { label: '해당되지 않아요.', value: 0 },
          { label: '현재 임산부에요.', next: 'jda', value: 'jda' },
          { label: '출산한 지 3개월 이내에요.', next: 'jdb', value: 'jdb' },
        ],
        children: {
          jda: {
            category: SurveyCategory.Middle,
            label: '임산부',
            selectType: SelectType.Check,
            answers: [
              { label: '해당 증상이 없어요.', value: 0 },
              { label: '입덧', next: 'jdaa', value: 'jdaa', symptomCode: ['S254'] },
              { label: '하혈', next: 'jdab', value: 'jdab', symptomCode: ['S256'] },
              { label: '임신중독증', next: 'jdac', value: 'jdac', symptomCode: ['S255'] },
              { label: '태동불안', next: 'jdad', value: 'jdad', symptomCode: ['S305'] },
              { label: '조기 진통', next: 'jdae', value: 'jdae', symptomCode: ['S306'] },
            ],
            children: {
              jdaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
              jdab: { category: SurveyCategory.End, selectType: SelectType.Scale },
              jdac: { category: SurveyCategory.End, selectType: SelectType.Scale },
              jdad: { category: SurveyCategory.End, selectType: SelectType.Scale },
              jdae: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
          jdb: {
            category: SurveyCategory.Middle,
            label: '산후',
            selectType: SelectType.Check,
            answers: [
              { label: '해당 증상이 없어요.', value: 0 },
              { label: '잔류 태반', next: 'jdba', value: 'jdba', symptomCode: ['S264'] },
              { label: '오로', next: 'jdbb', value: 'jdbb', symptomCode: ['S214'] },
              { label: '출산 후 복통', next: 'jdbc', value: 'jdbc', symptomCode: ['S152'] },
              { label: '소변 이상', next: 'jdbd', value: 'jdbd', symptomCode: ['S154'] },
              { label: '출산 후 부종', next: 'jdbe', value: 'jdbe', symptomCode: ['S153'] },
              { label: '출산 후 유방통', next: 'jdbf', value: 'jdbf', symptomCode: ['S155', 'S013'] },
              { label: '출산 후 발열', next: 'jdbg', value: 'jdbg', symptomCode: ['S151'] },
              { label: '출산 후 빈혈', next: 'jdbh', value: 'jdbh', symptomCode: ['S156'] },
              { label: '계류 유산', next: 'jdbi', value: 'jdbi', symptomCode: ['S149'] },
              { label: '조기 출산', next: 'jdbj', value: 'jdbj', symptomCode: ['S277'] },
            ],
            children: {
              jdba: { category: SurveyCategory.End, selectType: SelectType.Scale },
              jdbb: { category: SurveyCategory.End, selectType: SelectType.Scale },
              jdbc: { category: SurveyCategory.End, selectType: SelectType.Scale },
              jdbd: { category: SurveyCategory.End, selectType: SelectType.Scale },
              jdbe: { category: SurveyCategory.End, selectType: SelectType.Scale },
              jdbf: { category: SurveyCategory.End, selectType: SelectType.Scale },
              jdbg: { category: SurveyCategory.End, selectType: SelectType.Scale },
              jdbh: { category: SurveyCategory.End, selectType: SelectType.Scale },
              jdbi: { category: SurveyCategory.End, selectType: SelectType.Scale },
              jdbj: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
        },
      },
    },
  },
  k: {
    category: SurveyCategory.Main,
    label: '월경/불임',
    desc: ['현재 월경, 불임 관련 불편한 증상이 있으신가요?'],
    selectType: SelectType.Check,
    answers: [
      {
        label: '월경 관련하여 불편함이 있어요.',
        next: ['ka', 'kb'],
        value: 1,
        symptomCode: ['S231'],
      },
      { label: '불임 증상이 있어요.', next: ['ka', 'kb', 'kc'], value: 2, symptomCode: ['S141'] },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      ka: {
        category: SurveyCategory.Middle,
        label: '월경 주기',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '월경 주기가 평소보다 길어졌어요.', next: ['kaa'], value: 'kaa', symptomCode: ['S233'] },
          { label: '월경 주기가 평소보다 짧아졌어요.', next: ['kab'], value: 'kab', symptomCode: ['S770'] },
          {
            label: '최근 월경을 아예 안하거나, 매우 드물게 해요.',
            next: ['kac', 'kad'],
            value: 'kac',
            symptomCode: ['S019'],
          },
        ],
        children: {
          kaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kad: {
            category: SurveyCategory.End,
            label: '마지막 월경 일',
            desc: ['마지막 월경 일은 언제인가요?'],
            selectType: SelectType.Radio,
            answers: [
              { label: '2~3개월 전', value: 1, symptomCode: ['S814'] },
              { label: '3~6개월 전', value: 2, symptomCode: ['S815'] },
              { label: '6~12개월 전', value: 3, symptomCode: ['S816'] },
              { label: '1년 이상', value: 4, symptomCode: ['S817'] },
            ],
          },
        },
      },
      kb: {
        category: SurveyCategory.Middle,
        label: '월경',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '월경통이 심해요.', next: 'kba', value: 'kba', symptomCode: ['S308'] },
          { label: '월경 전 복통이 있어요.', next: 'kbb', value: 'kbb', symptomCode: ['S017'] },
          { label: '월경 전 유방통증이 있어요.', next: 'kbc', value: 'kbc', symptomCode: ['S018'] },
          {
            label: '월경 시 빈혈이 있어요.',
            next: 'kbd',
            value: 'kbd',
            symptomCode: ['S132'],
          },
          { label: '월경 양이 많아졌어요.', next: 'kbe', value: 'kbe', symptomCode: ['S230'] },
          { label: '월경 양이 적어졌어요.', next: 'kbf', value: 'kbf', symptomCode: ['S800'] },
          { label: '월경 색이 진하고 덩어리가 많아요.', next: 'kbg', value: 'kbg', symptomCode: ['S232'] },
        ],
        children: {
          kba: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kbb: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kbc: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kbd: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kbe: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kbf: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kbg: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      kc: {
        category: SurveyCategory.Middle,
        label: '자궁/생식기',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '부정 자궁 출혈', next: 'kca', value: 'kca', symptomCode: ['S142'] },
          { label: '자궁하수', next: 'kcb', value: 'kcb', symptomCode: ['S260'] },
          { label: '자궁근종, 신생물, 물혹 등', next: 'kcc', value: 'kcc', symptomCode: ['S130'] },
          { label: '냉이 많다.', next: 'kcd', value: 'kcd', symptomCode: ['S077'] },
          { label: '냉이 뿌예요.', next: 'kce', value: 'kce', symptomCode: ['S119'] },
          { label: '냉에 피가 비쳐요.', next: 'kcf', value: 'kcf', symptomCode: ['S078'] },
          { label: '아랫배가 차요.', next: 'kcg', value: 'kcg', symptomCode: ['S258'] },
          { label: '생식기가 가려워요.', next: 'kch', value: 'kch', symptomCode: ['S244'] },
        ],
        children: {
          kca: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kcb: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kcc: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kcd: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kce: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kcf: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kcg: { category: SurveyCategory.End, selectType: SelectType.Scale },
          kch: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
    },
  },
  l: {
    category: SurveyCategory.Main,
    label: '갱년기/유방',
    desc: ['해당 되는 증상을 골라주세요.'],
    selectType: SelectType.Check,
    answers: [
      { label: '해당 증상이 없어요.', value: 0 },
      { label: '갱년기', next: 'la', value: 'la' },
      { label: '유방통', next: 'lb', value: 'lb', symptomCode: ['S236'] },
      { label: '유방의 멍울/덩어리', next: 'lc', value: 'lc', symptomCode: ['S238', 'S240', 'S241'] },
    ],
    children: {
      la: {
        category: SurveyCategory.Middle,
        label: '갱년기',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '안면홍조', next: 'laa', value: 'laa', symptomCode: ['S335'] },
          { label: '등 뒤 땀', next: 'lab', value: 'lab', symptomCode: ['S824'] },
          { label: '우울감', next: 'lac', value: 'lac', symptomCode: ['S227'] },
          { label: '가슴 두근거림', next: 'lad', value: 'lad', symptomCode: ['S199'] },
          { label: '질 건조', next: 'lae', value: 'lae', symptomCode: ['S827'] },
        ],
        children: {
          laa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          lab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          lac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          lad: { category: SurveyCategory.End, selectType: SelectType.Scale },
          lae: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      lb: { category: SurveyCategory.End, selectType: SelectType.Scale },
      lc: { category: SurveyCategory.End, selectType: SelectType.Scale },
    },
  },
  m: {
    category: SurveyCategory.Main,
    label: '소아',
    desc: ['해당 되는 증상을 골라주세요.'],
    selectType: SelectType.Check,
    answers: [
      { label: '해당 증상이 없어요.', value: 0 },
      { label: '감기에 잘 걸려요.(허약)', next: 'ma', value: 'ma', symptomCode: ['S172'] },
      { label: '밥을 잘 안먹어요.', next: 'mb', value: 'mb', symptomCode: ['S175'] },
      { label: '또래에 비해 키가 작아요.', next: 'mc', value: 'mc', symptomCode: ['S172'] },
      { label: '영양실조', next: 'md', value: 'md', symptomCode: ['S008'] },
      { label: '볼거리', next: 'me', value: 'me', symptomCode: ['S261'] },
      { label: '소아마비', value: 1, symptomCode: ['S171'] },
      { label: '경련', next: 'mf', value: 'mf', symptomCode: ['S170'] },
    ],
    children: {
      ma: { category: SurveyCategory.End, selectType: SelectType.Scale },
      mb: { category: SurveyCategory.End, selectType: SelectType.Scale },
      mc: { category: SurveyCategory.End, selectType: SelectType.Scale },
      md: { category: SurveyCategory.End, selectType: SelectType.Scale },
      me: { category: SurveyCategory.End, selectType: SelectType.Scale },
      mf: { category: SurveyCategory.End, selectType: SelectType.Scale },
    },
  },
  A: {
    category: SurveyCategory.Main,
    label: '피로/면역력',
    desc: ['피로/면역력 관련하여 불편함이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      {
        label: '네, 있어요.',
        next: ['Aa', 'Ab', 'Ac', 'Ad', 'Ae', 'Af', 'Ag', 'Ah', 'Ai', 'Aj', 'Ak'],
        value: 1,
        symptomCode: ['S056', 'S273'],
      },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      Aa: {
        category: SurveyCategory.End,
        label: '수면 시간',
        desc: ['하루 평균 수면 시간을 선택해주세요.'],
        selectType: SelectType.Radio,
        answers: [
          { label: '6시간 미만', value: 1, symptomCode: ['S453', 'S138'] },
          { label: '6 ~ 7시간', value: 2, symptomCode: ['S454'] },
          { label: '7 ~ 8시간', value: 3, symptomCode: ['S455'] },
          { label: '8 ~ 9시간', value: 4, symptomCode: ['S456'] },
          { label: '9시간 이상', value: 5, symptomCode: ['S457'] },
        ],
      },
      Ab: {
        category: SurveyCategory.End,
        label: '식사 횟수',
        desc: ['하루 식사 횟수는 어떻게 되시나요?'],
        selectType: SelectType.Radio,
        answers: [
          { label: '1회', value: 1, symptomCode: ['S406'] },
          { label: '2회', value: 2, symptomCode: ['S407'] },
          { label: '3회', value: 3, symptomCode: ['S408'] },
          { label: '4회 이상', value: 4, symptomCode: ['S409'] },
        ],
      },
      Ac: {
        category: SurveyCategory.End,
        label: '식사간격',
        desc: ['평소 식사간격은 규척적이신가요?'],
        selectType: SelectType.Radio,
        answers: [
          { label: '매일 규칙적이에요.', value: 1, symptomCode: ['S410'] },
          { label: '가끔 불규칙적이에요.', value: 2, symptomCode: ['S411'] },
          { label: '불규칙적이에요.', value: 3, symptomCode: ['S412'] },
        ],
      },
      Ad: {
        category: SurveyCategory.End,
        label: '식사시간',
        desc: ['한 끼 식사를 하는데', '어느 정도 시간이 드시나요?'],
        selectType: SelectType.Radio,
        answers: [
          { label: '10분 미만', value: 1, symptomCode: ['S413'] },
          { label: '10~30분', value: 2, symptomCode: ['S414'] },
          { label: '30분 이상', value: 3, symptomCode: ['S415'] },
        ],
      },
      Ae: {
        category: SurveyCategory.End,
        label: '과식',
        desc: ['평소 과식을 얼마나 자주 하시나요?', '(평소 식사보다 많은 양을 섭취한 경우)'],
        selectType: SelectType.Radio,
        answers: [
          { label: '거의 하지 않아요.', value: 1, symptomCode: ['S416'] },
          { label: '주 1~3회', value: 2, symptomCode: ['S417'] },
          { label: '주 4~6회', value: 3, symptomCode: ['S418'] },
          { label: '매일 과식을 해요.', value: 4, symptomCode: ['S419'] },
        ],
      },
      Af: {
        category: SurveyCategory.End,
        label: '야식',
        desc: ['평소 야식은 얼마나 자주 드시나요?'],
        selectType: SelectType.Radio,
        answers: [
          { label: '거의 먹지 않는다.', value: 1, symptomCode: ['S420'] },
          { label: '주 1~3회', value: 2, symptomCode: ['S421'] },
          { label: '주 4~6회', value: 3, symptomCode: ['S422'] },
          { label: '매일 야식을 먹어요.', value: 4, symptomCode: ['S423'] },
        ],
      },
      Ag: {
        category: SurveyCategory.End,
        label: '중강도 운동',
        desc: [
          '숨이 약간 차거나 심장이 약간 빠르게 뛰는',
          '중강도 운동(빠르게 걷기, 요가)을',
          '얼마나 하시나요?',
        ],
        selectType: SelectType.Radio,
        answers: [
          { label: '거의 하지 않는다.', value: 1, symptomCode: ['S424'] },
          { label: '주 1~2회', value: 2, symptomCode: ['S425'] },
          { label: '주 3~4회', value: 3, symptomCode: ['S426'] },
          { label: '주 5회 이상', value: 4, symptomCode: ['S427'] },
        ],
      },
      Ah: {
        category: SurveyCategory.End,
        label: '고강도 운동',
        desc: [
          '숨이 많이 차거나 심장이 많이 빠르게 뛰는',
          '고강도 운동(빠르게 자전거 타기, 에어로빅)을',
          '얼마나 하시나요?',
        ],
        selectType: SelectType.Radio,
        answers: [
          { label: '거의 하지 않는다.', value: 1, symptomCode: ['S428'] },
          { label: '주 1~2회', value: 2, symptomCode: ['S429'] },
          { label: '주 3~4회', value: 3, symptomCode: ['S430'] },
          { label: '주 5회 이상', value: 4, symptomCode: ['S431'] },
        ],
      },
      Ai: {
        category: SurveyCategory.Middle,
        label: '흡연',
        desc: ['흡연을 하시나요?', '(연초, 전자담배 포함)'],
        selectType: SelectType.Radio,
        answers: [
          { label: '아니요.', value: 0 },
          {
            label: '이전에 피우다 현재는 금연 중이에요.',
            next: ['Aia', 'Aib'],
            value: 1,
            symptomCode: ['S432'],
          },
          { label: '예, 흡연을 하고 있어요.', next: ['Aia', 'Aib'], value: 2, symptomCode: ['S433'] },
        ],
        children: {
          Aia: {
            category: SurveyCategory.End,
            label: '흡연 기간',
            desc: ['흡연 기간은 어떻게 되시나요?'],
            selectType: SelectType.Radio,
            answers: [
              { label: '1년 미만', value: 1, symptomCode: ['S434'] },
              { label: '1~5년', value: 2, symptomCode: ['S435'] },
              { label: '6~10년', value: 3, symptomCode: ['S436'] },
              { label: '10년 이상', value: 4, symptomCode: ['S437'] },
            ],
          },
          Aib: {
            category: SurveyCategory.End,
            label: '흡연량',
            desc: ['하루평균 흡연량이 어떻게 되시나요?'],
            selectType: SelectType.Radio,
            answers: [
              { label: '10개피 미만', value: 1, symptomCode: ['S438'] },
              { label: '10개피 ~ 1갑', value: 2, symptomCode: ['S439'] },
              { label: '1~2갑', value: 3, symptomCode: ['S440'] },
              { label: '2갑 이상', value: 4, symptomCode: ['S441'] },
            ],
          },
        },
      },
      Aj: {
        category: SurveyCategory.Middle,
        label: '음주',
        desc: ['음주를 얼마나 자주 하시나요?'],
        selectType: SelectType.Radio,
        answers: [
          { label: '거의 마시지 않아요.', value: 0 },
          { label: '주 1~3회', next: ['Aja'], value: 1, symptomCode: ['S442'] },
          { label: '주 4~6회', next: ['Aja'], value: 2, symptomCode: ['S443'] },
          { label: '매일 마셔요.', next: ['Aja'], value: 3, symptomCode: ['S444'] },
        ],
        children: {
          Aja: {
            category: SurveyCategory.End,
            label: '음주량',
            desc: [
              '평균 음주량은 어떻게 되시나요?',
              '(소주 1병(330ml) = 7잔,',
              ' 맥주 1캔(335ml) = 2잔,',
              ' 맥주 1병(500ml) = 3잔)',
            ],
            selectType: SelectType.Radio,
            answers: [
              { label: '1~2잔', value: 1, symptomCode: ['S445'] },
              { label: '3~4잔', value: 2, symptomCode: ['S446'] },
              { label: '5~6잔', value: 3, symptomCode: ['S447'] },
              { label: '7~9잔', value: 4, symptomCode: ['S448'] },
              { label: '10잔 이상', value: 5, symptomCode: ['S449'] },
            ],
          },
        },
      },
      Ak: {
        category: SurveyCategory.End,
        label: '커피',
        desc: ['하루 평균 커피를 얼마나 드시나요?'],
        selectType: SelectType.Radio,
        answers: [
          { label: '커피를 마시지 않아요.', value: 0 },
          { label: '1~2잔', value: 1, symptomCode: ['S450'] },
          { label: '3~4잔', value: 2, symptomCode: ['S451'] },
          { label: '5잔 이상', value: 3, symptomCode: ['S452'] },
        ],
      },
    },
  },
  n: {
    category: SurveyCategory.Main,
    label: '머리/뒷목',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['na', 'nb'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      na: {
        category: SurveyCategory.Middle,
        label: '머리/뒷목',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '두통', next: 'naa', value: 'naa' },
          { label: '어지럼증', next: 'nab', value: 'nab', symptomCode: ['S325'] },
        ],
        children: {
          naa: {
            category: SurveyCategory.Middle,
            label: '두통 부위',
            desc: ['두통이 있는 부위는 어디인가요?'],
            selectType: SelectType.Check,
            answers: [
              { label: '눈 주위', next: 'naaa', value: 'naaa', symptomCode: ['S312'] },
              { label: '귀 주위', next: 'naab', value: 'naab', symptomCode: ['S312'] },
              { label: '이마', next: 'naac', value: 'naac', symptomCode: ['S087'] },
              { label: '정수리', next: 'naad', value: 'naad', symptomCode: ['S087'] },
              { label: '뒷 목', next: 'naae', value: 'naae', symptomCode: ['S087'] },
              { label: '전체', next: 'naaf', value: 'naaf', symptomCode: ['S087'] },
            ],
            children: {
              naaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
              naab: { category: SurveyCategory.End, selectType: SelectType.Scale },
              naac: { category: SurveyCategory.End, selectType: SelectType.Scale },
              naad: { category: SurveyCategory.End, selectType: SelectType.Scale },
              naae: { category: SurveyCategory.End, selectType: SelectType.Scale },
              naaf: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
          nab: {
            category: SurveyCategory.Middle,
            label: '어지럼증 양상',
            desc: ['어지럼증 양상은 어떠한가요?'],
            selectType: SelectType.Check,
            answers: [
              { label: '가만히 있어도 어지러워요.', next: 'naba', value: 'naba', symptomCode: ['S709'] },
              { label: '앉았다 일어서면 어지러워요.', next: 'nabb', value: 'nabb', symptomCode: ['S710'] },
              { label: '기타(주관식)', value: -1 },
            ],
            children: {
              naba: { category: SurveyCategory.End, selectType: SelectType.Scale },
              nabb: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
        },
      },
      nb: {
        category: SurveyCategory.Middle,
        label: '머리/뒷목 질환',
        desc: ['현재 앓고 계신 질환을 모두 골라주세요.'],
        selectType: SelectType.Check,
        answers: [
          { label: '앓고 있는 질환이 없어요.', value: 0 },
          {
            label: '군발성 두통',
            next: 'nba',
            value: 'naa',
            symptomCode: ['S701'],
          },
          {
            label: '긴장성 두통',
            next: 'nbb',
            value: 'nab',
            symptomCode: ['S702'],
          },
          { label: '편두통', next: 'nbc', value: 'nac', symptomCode: ['S703', 'S312'] },
          { label: '빈혈', next: 'nbd', value: 'nad', symptomCode: ['S704'] },
          { label: '이석증', next: 'nbe', value: 'nae', symptomCode: ['S705'] },
          { label: '메니에르병', next: 'nbf', value: 'naf', symptomCode: ['S706'] },
          { label: '중풍후유증', next: 'nbg', value: 'nag', symptomCode: ['S707'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          nba: { category: SurveyCategory.End, selectType: SelectType.Onset },
          nbb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          nbc: { category: SurveyCategory.End, selectType: SelectType.Onset },
          nbd: { category: SurveyCategory.End, selectType: SelectType.Onset },
          nbe: { category: SurveyCategory.End, selectType: SelectType.Onset },
          nbf: { category: SurveyCategory.End, selectType: SelectType.Onset },
          nbg: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
  o: {
    category: SurveyCategory.Main,
    label: '귀',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['oa', 'ob'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      oa: {
        category: SurveyCategory.Middle,
        label: '귀',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '귀에서 진물이 나와요.', next: 'oaa', value: 'oaa', symptomCode: ['S248'] },
          { label: '잘 들리지 않아요.(난청)', next: 'oab', value: 'oab', symptomCode: ['S249'] },
          { label: '이명이 있어요.', next: 'oac', value: 'oac', symptomCode: ['S250'] },
        ],
        children: {
          oaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          oab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          oac: {
            category: SurveyCategory.End,
            label: '이명 양상',
            desc: ['이명의 양상은 어떠한가요?'],
            selectType: SelectType.Check,
            answers: [
              { label: '시냇물 흐르는 소리가 들려요.', next: 'oaca', value: 'oaca', symptomCode: ['S711'] },
              { label: `삐 하는 소리가 들려요.`, next: 'oacb', value: 'oacb', symptomCode: ['S712'] },
              { label: '바람소리가 들려요.', next: 'oacc', value: 'oacc', symptomCode: ['S713'] },
              { label: '기타(주관식)', value: -1 },
            ],
            children: {
              oaca: { category: SurveyCategory.End, selectType: SelectType.Scale },
              oacb: { category: SurveyCategory.End, selectType: SelectType.Scale },
              oacc: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
        },
      },
      ob: {
        category: SurveyCategory.Middle,
        label: '귀 질환',
        desc: ['현재 앓고 계신 질환을 모두 골라주세요.'],
        selectType: SelectType.Check,
        answers: [
          { label: '앓고 있는 질환이 없어요.', value: 0 },
          { label: '중이염', next: 'oba', value: 'oba', symptomCode: ['S714'] },
          { label: '외이도염', next: 'obb', value: 'obb', symptomCode: ['S715'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          oba: { category: SurveyCategory.End, selectType: SelectType.Onset },
          obb: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
  p: {
    category: SurveyCategory.Main,
    label: '눈',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['pa', 'pb'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      pa: {
        category: SurveyCategory.Middle,
        label: '눈',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '눈이 침침해요.', next: 'paa', value: 'paa', symptomCode: ['S107'] },
          { label: '눈부심', next: 'pab', value: 'pab', symptomCode: ['S181'] },
          { label: '눈물이 많이 나요.', next: 'pac', value: 'pac', symptomCode: ['S068'] },
          { label: '눈 충혈', next: 'pad', value: 'pad', symptomCode: ['S109'] },
          { label: '눈에 통증이 있어요.', next: 'pae', value: 'pae', symptomCode: ['S110'] },
          {
            label: '눈꺼풀에 염증이 있어요.(안검연염 등)',
            next: 'paf',
            value: 'paf',
            symptomCode: ['S090', 'S268'],
          },
        ],
        children: {
          paa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          pab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          pac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          pad: { category: SurveyCategory.End, selectType: SelectType.Scale },
          pae: { category: SurveyCategory.End, selectType: SelectType.Scale },
          paf: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      pb: {
        category: SurveyCategory.Middle,
        label: '눈 질환',
        desc: ['현재 앓고 계신 질환을 모두 골라주세요.'],
        selectType: SelectType.Check,
        answers: [
          { label: '앓고 있는 질환이 없어요.', value: 0 },
          { label: '녹내장', next: 'pba', value: 'pba', symptomCode: ['S719'] },
          { label: '백내장', next: 'pbb', value: 'pbb', symptomCode: ['S108'] },
          {
            label: '비문증(눈 앞에 물체가 떠다님)',
            next: 'pbc',
            value: 'pbc',
            symptomCode: ['S111'],
          },
          { label: '야맹증', next: 'pbd', value: 'pbd', symptomCode: ['S203'] },
          { label: '안구건조증', next: 'pbe', value: 'pbe', symptomCode: ['S720'] },
          { label: '노안', next: 'pbf', value: 'pbf', symptomCode: ['S721'] },
          { label: '결막염', next: 'pbg', value: 'pbg', symptomCode: ['S109'] },
          { label: '황반변성', next: 'pbh', value: 'pbh', symptomCode: ['S722'] },
          { label: '눈 다래끼', next: 'pbi', value: 'pbi', symptomCode: ['S061'] },
          { label: '포도막염', next: 'pbj', value: 'pbj', symptomCode: ['S723'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          pba: { category: SurveyCategory.End, selectType: SelectType.Onset },
          pbb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          pbc: { category: SurveyCategory.End, selectType: SelectType.Onset },
          pbd: { category: SurveyCategory.End, selectType: SelectType.Onset },
          pbe: { category: SurveyCategory.End, selectType: SelectType.Onset },
          pbf: { category: SurveyCategory.End, selectType: SelectType.Onset },
          pbg: { category: SurveyCategory.End, selectType: SelectType.Onset },
          pbh: { category: SurveyCategory.End, selectType: SelectType.Onset },
          pbi: { category: SurveyCategory.End, selectType: SelectType.Onset },
          pbj: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
  q: {
    category: SurveyCategory.Main,
    label: '코',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['qa', 'qb'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      qa: {
        category: SurveyCategory.Middle,
        label: '코',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '코피가 나요.(외상 제외)', next: 'qaa', value: 'qaa', symptomCode: ['S299'] },
          { label: '콧물이 나요.', next: 'qab', value: 'qab', symptomCode: ['S300'] },
          { label: '코가 막혀요.', next: 'qac', value: 'qac', symptomCode: ['S298'] },
          { label: '코에 폴립이 있어요.', next: 'qad', value: 'qad', symptomCode: ['S144'] },
        ],
        children: {
          qaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          qab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          qac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          qad: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      qb: {
        category: SurveyCategory.Middle,
        label: '코 질환',
        desc: ['현재 앓고 계신 질환을 모두 골라주세요.'],
        selectType: SelectType.Check,
        answers: [
          { label: '앓고 있는 질환이 없어요.', value: 0 },
          { label: '비염', next: 'qba', value: 'qba', symptomCode: ['S298', 'S300'] },
          { label: '축농증', next: 'qbb', value: 'qbb', symptomCode: ['S298', 'S300'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          qba: { category: SurveyCategory.End, selectType: SelectType.Onset },
          qbb: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
  r: {
    category: SurveyCategory.Main,
    label: '입/혀',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['ra'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      ra: {
        category: SurveyCategory.Middle,
        label: '입/혀',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '입이 써요.', next: 'raa', value: 'raa', symptomCode: ['S037'] },
          { label: '입이 바짝바짝 말라요.', next: 'rab', value: 'rab', symptomCode: ['S041'] },
          { label: '혀가 마르고 갈라져요.', next: 'rac', value: 'rac', symptomCode: ['S160'] },
          {
            label: '입, 입술이 잘 헐어요.(구내염, 헤르페스 등)',
            next: 'rad',
            value: 'rad',
            symptomCode: ['S042'],
          },
          { label: '혀가 잘 헐어요.', next: 'rae', value: 'rae', symptomCode: ['S161'] },
          { label: '침이 많아요.', next: 'raf', value: 'raf', symptomCode: ['S070'] },
          { label: '구안와사(안면신경마비)', next: 'rag', value: 'rag', symptomCode: ['S039'] },
        ],
        children: {
          raa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          rab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          rac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          rad: { category: SurveyCategory.End, selectType: SelectType.Scale },
          rae: { category: SurveyCategory.End, selectType: SelectType.Scale },
          raf: { category: SurveyCategory.End, selectType: SelectType.Scale },
          rag: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
    },
  },
  s: {
    category: SurveyCategory.Main,
    label: '치아/잇몸',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['sa'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      sa: {
        category: SurveyCategory.Middle,
        label: '치아/잇몸',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '잇몸이 붓고 아파요.', next: 'saa', value: 'saa', symptomCode: ['S294'] },
          { label: '이가 아프고 흔들려요.', next: 'sab', value: 'sab', symptomCode: ['S295'] },
          { label: '충치가 있어요.', next: 'sac', value: 'sac', symptomCode: ['S293'] },
          { label: '치통이 있어요.', next: 'sad', value: 'sad', symptomCode: ['S297'] },
          { label: '입냄새가 나요.', next: 'sae', value: 'sae', symptomCode: ['S043'] },
        ],
        children: {
          saa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          sab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          sac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          sad: { category: SurveyCategory.End, selectType: SelectType.Scale },
          sae: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
    },
  },
  t: {
    category: SurveyCategory.Main,
    label: '목/인후',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['ta', 'tb'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      ta: {
        category: SurveyCategory.Middle,
        label: '목/인후',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          {
            label: '목에 무언가 걸린 듯 이물감이 있어요.(매핵기)',
            next: 'taa',
            value: 'taa',
            symptomCode: ['S100'],
          },
          { label: '목 안이 붓고 침 삼키기가 어려워요.', next: 'tab', value: 'tab', symptomCode: ['S252'] },
          {
            label: '목이 쉬었거나 목소리가 잘 나지 않아요.',
            next: 'tac',
            value: 'tac',
            symptomCode: ['S245'],
          },
        ],
        children: {
          taa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          tab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          tac: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      tb: {
        category: SurveyCategory.Middle,
        label: '목/인후 질환',
        desc: ['현재 앓고 계신 질환을 모두 골라주세요.'],
        selectType: SelectType.Check,
        answers: [
          { label: '앓고 있는 질환이 없어요.', value: 0 },
          { label: '림프절 비대', next: 'tba', value: 'tba', symptomCode: ['S092', 'S076'] },
          { label: '갑상선 비대', next: 'tbb', value: 'tbb', symptomCode: ['S009', 'S057'] },
          { label: '인후염', next: 'tbc', value: 'tbc', symptomCode: ['S251', 'S253'] },
          { label: '식도염', next: 'tbd', value: 'tbd', symptomCode: ['S708'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          tba: { category: SurveyCategory.End, selectType: SelectType.Onset },
          tbb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          tbc: { category: SurveyCategory.End, selectType: SelectType.Onset },
          tbd: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
  u: {
    category: SurveyCategory.Main,
    label: '기침/가래',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['ua', 'ub'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      ua: {
        category: SurveyCategory.Middle,
        label: '기침/가래',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '기침', next: 'uaa', value: 'uaa' },
          { label: '가래', next: 'uab', value: 'uab' },
        ],
        children: {
          uaa: {
            category: SurveyCategory.Middle,
            label: '기침 양상',
            desc: ['기침의 양상은 어떠한가요?'],
            selectType: SelectType.Check,
            answers: [
              {
                label: '콧물, 가래를 동반한 기침을 해요.',
                next: 'uaaa',
                value: 'uaaa',
                symptomCode: ['S054'],
              },
              { label: '최근에 마른 기침을 자주 해요.', next: 'uaab', value: 'uaab', symptomCode: ['S093'] },
              {
                label: '기침을 오랜 기간(6개월 이상)했고 몸에 힘이 없어요',
                next: 'uaac',
                value: 'uaac',
                symptomCode: ['S060'],
              },
              { label: '기타(주관식)', value: -1 },
            ],
            children: {
              uaaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
              uaab: { category: SurveyCategory.End, selectType: SelectType.Scale },
              uaac: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
          uab: {
            category: SurveyCategory.Middle,
            label: '가래 양상',
            desc: ['가래의 양상은 어떠한가요?'],
            selectType: SelectType.Check,
            answers: [
              { label: '가래가 많아요.', next: 'uaba', value: 'uaba', symptomCode: ['S002'] },
              { label: '가래가 누런색이에요.', next: 'uabb', value: 'uabb', symptomCode: ['S337'] },
              { label: '가래가 끈끈하고 잘 안뱉어져요.', next: 'uabc', value: 'uabc', symptomCode: ['S278'] },
              { label: '가래가 묽고 거품이 있어요.', next: 'uabd', value: 'uabd', symptomCode: ['S342'] },
              { label: '가래에 피가 섞여있어요.', next: 'uabe', value: 'uabe', symptomCode: ['S004'] },
              { label: '기타(주관식)', value: -1 },
            ],
            children: {
              uaba: { category: SurveyCategory.End, selectType: SelectType.Scale },
              uabb: { category: SurveyCategory.End, selectType: SelectType.Scale },
              uabc: { category: SurveyCategory.End, selectType: SelectType.Scale },
              uabd: { category: SurveyCategory.End, selectType: SelectType.Scale },
              uabe: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
        },
      },
      ub: {
        category: SurveyCategory.Middle,
        label: '기침/가래 질환',
        desc: ['현재 앓고 계신 질환을 모두 골라주세요.'],
        selectType: SelectType.Check,
        answers: [
          { label: '앓고 있는 질환이 없어요.', value: 0 },
          { label: '감기', next: 'uba', value: 'uba', symptomCode: ['S007'] },
          { label: '기관지염', next: 'ubb', value: 'ubb', symptomCode: ['S049'] },
          { label: '폐렴', next: 'ubc', value: 'ubc', symptomCode: ['S314'] },
          { label: '폐결핵', next: 'ubd', value: 'ubd', symptomCode: ['S313', 'S029'] },
          { label: 'COPD(만성폐쇄성)', next: 'ube', value: 'ube', symptomCode: ['S001'] },
          { label: '백일해', next: 'ubf', value: 'ubf', symptomCode: ['S112'] },
          { label: '천식', next: 'ubg', value: 'ubg', symptomCode: ['S289'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          uba: { category: SurveyCategory.End, selectType: SelectType.Onset },
          ubb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          ubc: { category: SurveyCategory.End, selectType: SelectType.Onset },
          ubd: { category: SurveyCategory.End, selectType: SelectType.Onset },
          ube: { category: SurveyCategory.End, selectType: SelectType.Onset },
          ubf: { category: SurveyCategory.End, selectType: SelectType.Onset },
          ubg: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
  v: {
    category: SurveyCategory.Main,
    label: '흉/호흡',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['va', 'vb'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      va: {
        category: SurveyCategory.Middle,
        label: '흉/호흡',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '가슴이 답답해요.', next: 'vaa', value: 'vaa', symptomCode: ['S339'] },
          { label: '가슴 쪽에 열감이 있어요.', next: 'vab', value: 'vab', symptomCode: ['S340'] },
          { label: '가슴이 뻐근하거나 통증이 있어요.', next: 'vac', value: 'vac', symptomCode: ['S341'] },
          {
            label: '숨이 잘 안쉬어져요.(숨쉬기가 불편해요.)',
            next: 'vad',
            value: 'vad',
            symptomCode: ['S331'],
          },
        ],
        children: {
          vaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          vab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          vac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          vad: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      vb: {
        category: SurveyCategory.Middle,
        label: '흉/호흡 질환',
        desc: ['현재 앓고 계신 질환을 모두 골라주세요.'],
        selectType: SelectType.Check,
        answers: [
          { label: '앓고 있는 질환이 없어요.', value: 0 },
          { label: '협심증', next: 'vba', value: 'vba', symptomCode: ['S329', 'S036'] },
          { label: '심근경색', next: 'vbb', value: 'vbb', symptomCode: ['S036'] },
          { label: '부정맥', next: 'vbc', value: 'vbc', symptomCode: ['S134'] },
          { label: '저혈압', next: 'vbd', value: 'vbd', symptomCode: ['S724'] },
          { label: '고혈압', next: 'vbe', value: 'vbe', symptomCode: ['S023'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          vba: { category: SurveyCategory.End, selectType: SelectType.Onset },
          vbb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          vbc: { category: SurveyCategory.End, selectType: SelectType.Onset },
          vbd: { category: SurveyCategory.End, selectType: SelectType.Onset },
          vbe: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
  w: {
    category: SurveyCategory.Main,
    label: '복부',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['wa', 'wb'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      wa: {
        category: SurveyCategory.Middle,
        label: '복부',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '윗배(배꼽 위)가 아파요.', next: 'waa', value: 'waa', symptomCode: ['S218'] },
          { label: '윗배가 답답하고 빵빵해요.', next: 'wab', value: 'wab', symptomCode: ['S219'] },
          { label: '옆구리가 결리고 아파요.', next: 'wac', value: 'wac', symptomCode: ['S330'] },
          { label: '아랫배(배꼽 아래)가 아파요.', next: 'wad', value: 'wad', symptomCode: ['S131'] },
          { label: '배에 가스가 차고 빵빵해요.', next: 'wae', value: 'wae', symptomCode: ['S143'] },
          { label: '배에서 꾸르륵 소리가 나요.', next: 'waf', value: 'waf', symptomCode: ['S265'] },
          { label: '아랫배에 덩어리가 만져져요.', next: 'wag', value: 'wag', symptomCode: ['S285'] },
        ],
        children: {
          waa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          wab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          wac: { category: SurveyCategory.End, selectType: SelectType.Scale },
          wad: { category: SurveyCategory.End, selectType: SelectType.Scale },
          wae: { category: SurveyCategory.End, selectType: SelectType.Scale },
          waf: { category: SurveyCategory.End, selectType: SelectType.Scale },
          wag: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      wb: {
        category: SurveyCategory.Middle,
        label: '복부 질환',
        desc: ['현재 앓고 계신 질환을 모두 골라주세요.'],
        selectType: SelectType.Check,
        answers: [
          { label: '앓고 있는 질환이 없어요.', value: 0 },
          { label: '역류성식도염', next: 'wba', value: 'wba', symptomCode: ['S726'] },
          { label: '위염', next: 'wbb', value: 'wbb', symptomCode: ['S727'] },
          { label: '위십이지장궤양', next: 'wbc', value: 'wbc', symptomCode: ['S179'] },
          { label: '위하수', next: 'wbd', value: 'wbd', symptomCode: ['S234'] },
          { label: '위확장', next: 'wbe', value: 'wbe', symptomCode: ['S235'] },
          { label: '간염', next: 'wbf', value: 'wbf', symptomCode: ['S728'] },
          { label: '지방간', next: 'wbg', value: 'wbg', symptomCode: ['S729'] },
          { label: '간경화', next: 'wbh', value: 'wbh', symptomCode: ['S730'] },
          { label: '담낭염', next: 'wbi', value: 'wbi', symptomCode: ['S731'] },
          { label: '담석증', next: 'wbj', value: 'wbj', symptomCode: ['S732'] },
          { label: '복막염', next: 'wbk', value: 'wbk', symptomCode: ['S267'] },
          { label: '복수', next: 'wbl', value: 'wbl', symptomCode: ['S129'] },
          { label: '충수염', next: 'wbm', value: 'wbm', symptomCode: ['S267'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          wba: { category: SurveyCategory.End, selectType: SelectType.Onset },
          wbb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          wbc: { category: SurveyCategory.End, selectType: SelectType.Onset },
          wbd: { category: SurveyCategory.End, selectType: SelectType.Onset },
          wbe: { category: SurveyCategory.End, selectType: SelectType.Onset },
          wbf: { category: SurveyCategory.End, selectType: SelectType.Onset },
          wbg: { category: SurveyCategory.End, selectType: SelectType.Onset },
          wbh: { category: SurveyCategory.End, selectType: SelectType.Onset },
          wbi: { category: SurveyCategory.End, selectType: SelectType.Onset },
          wbj: { category: SurveyCategory.End, selectType: SelectType.Onset },
          wbk: { category: SurveyCategory.End, selectType: SelectType.Onset },
          wbl: { category: SurveyCategory.End, selectType: SelectType.Onset },
          wbm: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
  x: {
    category: SurveyCategory.Main,
    label: '피부',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['xa', 'xb'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      xa: {
        category: SurveyCategory.Middle,
        label: '피부',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '아토피/습진', next: 'xaa', value: 'xaa', symptomCode: ['S189', 'S190', 'S050'] },
          { label: '여드름/농포', next: 'xab', value: 'xab', symptomCode: ['S209', 'S280', 'S062', 'S118'] },
          { label: '버짐/각질/백선', next: 'xac', value: 'xac', symptomCode: ['S121'] },
          { label: '두드러기', next: 'xad', value: 'xad', symptomCode: ['S083'] },
          { label: '적색 반점(홍반)', next: 'xae', value: 'xae', symptomCode: ['S332', 'S072'] },
          { label: '백색 반점(백반)', next: 'xaf', value: 'xaf', symptomCode: ['S120'] },
          { label: '건선', next: 'xag', value: 'xag', symptomCode: ['S760'] },
          { label: '피부 궤양', next: 'xah', value: 'xah', symptomCode: ['S287', 'S316'] },
          { label: '주사비(딸기코)', next: 'xai', value: 'xai', symptomCode: ['S281'] },
          { label: '가려움증(소양감)', next: 'xaj', value: 'xaj', symptomCode: ['S177'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          xaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          xab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          xac: {
            category: SurveyCategory.End,
            label: '버짐/각질/백선 부위',
            desc: ['버짐/각질/백선 부위를 골라주세요.'],
            selectType: SelectType.Check,
            answers: [
              { label: '머리/두피', next: 'xaca', value: 'xaca', symptomCode: ['S085', 'S283'] },
              { label: '몸(체부)', next: 'xacb', value: 'xacb', symptomCode: ['S290'] },
              { label: '손발', next: 'xacc', value: 'xacc', symptomCode: ['S201'] },
              { label: '사타구니', next: 'xacd', value: 'xacd', symptomCode: ['S220'] },
              { label: '기타(주관식)', value: -1 },
            ],
            children: {
              xaca: { category: SurveyCategory.End, selectType: SelectType.Scale },
              xacb: { category: SurveyCategory.End, selectType: SelectType.Scale },
              xacc: { category: SurveyCategory.End, selectType: SelectType.Scale },
              xacd: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
          xad: { category: SurveyCategory.End, selectType: SelectType.Scale },
          xae: { category: SurveyCategory.End, selectType: SelectType.Scale },
          xaf: { category: SurveyCategory.End, selectType: SelectType.Scale },
          xag: { category: SurveyCategory.End, selectType: SelectType.Scale },
          xah: { category: SurveyCategory.End, selectType: SelectType.Scale },
          xai: { category: SurveyCategory.End, selectType: SelectType.Scale },
          xaj: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
      xb: {
        category: SurveyCategory.Middle,
        label: '피부 질환',
        desc: ['현재 앓고 계신 질환을 모두 골라주세요.'],
        selectType: SelectType.Check,
        answers: [
          { label: '앓고 있는 질환이 없어요.', value: 0 },
          { label: '화상', next: 'xba', value: 'xba', symptomCode: ['S335'] },
          { label: '동상', next: 'xbb', value: 'xbb', symptomCode: ['S081'] },
          { label: '매독', next: 'xbc', value: 'xbc', symptomCode: ['S099'] },
          { label: '풍진', next: 'xbd', value: 'xbd', symptomCode: ['S315'] },
          { label: '파상풍', next: 'xbe', value: 'xbe', symptomCode: ['S311'] },
          { label: '버거씨병', next: 'xbf', value: 'xbf', symptomCode: ['S124'] },
          { label: '한센병', next: 'xbg', value: 'xbg', symptomCode: ['S058'] },
          { label: '무좀', next: 'xbh', value: 'xbh', symptomCode: ['S262'] },
          { label: '사마귀', next: 'xbi', value: 'xbi', symptomCode: ['S148'] },
          { label: '홍역', next: 'xbj', value: 'xbj', symptomCode: ['S095'] },
          { label: '땀띠', next: 'xbk', value: 'xbk', symptomCode: ['S146'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          xba: { category: SurveyCategory.End, selectType: SelectType.Onset },
          xbb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          xbc: { category: SurveyCategory.End, selectType: SelectType.Onset },
          xbd: { category: SurveyCategory.End, selectType: SelectType.Onset },
          xbe: { category: SurveyCategory.End, selectType: SelectType.Onset },
          xbf: { category: SurveyCategory.End, selectType: SelectType.Onset },
          xbg: { category: SurveyCategory.End, selectType: SelectType.Onset },
          xbh: { category: SurveyCategory.End, selectType: SelectType.Onset },
          xbi: { category: SurveyCategory.End, selectType: SelectType.Onset },
          xbj: { category: SurveyCategory.End, selectType: SelectType.Onset },
          xbk: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
  y: {
    category: SurveyCategory.Main,
    label: '모발',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['ya'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      ya: {
        category: SurveyCategory.Middle,
        label: '모발',
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '원형탈모', next: 'yaa', value: 'yaa', symptomCode: ['S229'] },
          { label: '탈모(원형탈모 외)', next: 'yab', value: 'yab', symptomCode: ['S303'] },
          { label: '새치/흰머리', next: 'yac', value: 'yac', symptomCode: ['S182'] },
        ],
        children: {
          yaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
          yab: { category: SurveyCategory.End, selectType: SelectType.Scale },
          yac: { category: SurveyCategory.End, selectType: SelectType.Scale },
        },
      },
    },
  },
  z: {
    category: SurveyCategory.Main,
    label: '근골',
    desc: ['불편한 점이 있으신가요?'],
    selectType: SelectType.Radio,
    answers: [
      { label: '네, 있어요.', next: ['za', 'zb'], value: 1 },
      { label: '아니요, 없어요.', value: 0 },
    ],
    children: {
      za: {
        category: SurveyCategory.Middle,
        label: '근골',
        desc: ['평소 아픈 부위를 선택해주세요.'],
        selectType: SelectType.Check,
        answers: [
          { label: '해당 증상이 없어요.', value: 0 },
          { label: '목/어깨/등', next: 'zaa', value: 'zaa' },
          { label: '허리/골반', next: 'zab', value: 'zab' },
          { label: '손가락/손목/팔꿈치(관절)', next: 'zac', value: 'zac' },
          { label: '무릎/발목(관절)', next: 'zad', value: 'zad' },
          { label: '팔/다리(근육)', next: 'zae', value: 'zae' },
        ],
        children: {
          zaa: {
            category: SurveyCategory.Middle,
            label: '목/어깨/등',
            selectType: SelectType.Check,
            answers: [
              { label: '팔, 어깨가 쑤시고 아파요.', next: 'zaaa', value: 'zaaa', symptomCode: ['S012'] },
              { label: '뒷목이 아프고 뻣뻣해요.', next: 'zaab', value: 'zaab', symptomCode: ['S020'] },
              {
                label: '등, 어깨(승모근)가 시큰거리고 아파요.',
                next: 'zaac',
                value: 'zaac',
                symptomCode: ['S011'],
              },
              { label: '기타(주관식)', value: -1 },
            ],
            children: {
              zaaa: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zaab: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zaac: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
          zab: {
            category: SurveyCategory.Middle,
            label: '허리/골반',
            selectType: SelectType.Check,
            answers: [
              { label: '허리/골반이 시리고 시큰거려요.', next: 'zaba', value: 'zaba', symptomCode: ['S224'] },
              { label: '허리/골반이 쑤시고 아파요.', next: 'zabb', value: 'zabb', symptomCode: ['S226'] },
              { label: '뻣뻣하고 잘 움직여지지 않아요.', next: 'zabc', value: 'zabc', symptomCode: ['S031'] },
              { label: '기타(주관식)', value: -1 },
            ],
            children: {
              zaba: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zabb: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zabc: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
          zac: {
            category: SurveyCategory.Middle,
            label: '손가락/손목/팔꿈치',
            selectType: SelectType.Check,
            answers: [
              { label: '시리고 시큰거려요.', next: 'zaca', value: 'zaca', symptomCode: ['S774'] },
              { label: '관절이 쑤시고 아파요.', next: 'zacb', value: 'zacb', symptomCode: ['S033'] },
              {
                label: '뻣뻣하고 잘 움직여지지 않아요.',
                next: 'zacc',
                value: 'zacc',
                symptomCode: ['S031'],
              },
              { label: '기타(주관식)', value: -1 },
            ],
            children: {
              zaca: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zacb: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zacc: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
          zad: {
            category: SurveyCategory.Middle,
            label: '무릎/발목',
            selectType: SelectType.Check,
            answers: [
              { label: '무릎/발목이 시리고 시큰거려요.', next: 'zada', value: 'zada', symptomCode: ['S186'] },
              { label: '무릎/발목이 쑤시고 아파요.', next: 'zadb', value: 'zadb', symptomCode: ['S187'] },
              {
                label: '뻣뻣하고 잘 움직여지지 않아요.',
                next: 'zadc',
                value: 'zadc',
                symptomCode: ['S031'],
              },
              { label: '다리에 힘이 없어요.', next: 'zadd', value: 'zadd', symptomCode: ['S320'] },
              { label: '기타(주관식)', value: -1 },
            ],
            children: {
              zada: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zadb: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zadc: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zadd: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
          zae: {
            category: SurveyCategory.Middle,
            label: '팔/다리',
            selectType: SelectType.Check,
            answers: [
              { label: '근육에 힘이 없어요.', next: 'zaea', value: 'zaea', symptomCode: ['S045'] },
              { label: '근육이 떨려요.', next: 'zaeb', value: 'zaeb', symptomCode: ['S210'] },
              { label: '근육이 당기고 오그라들어요.', next: 'zaec', value: 'zaec', symptomCode: ['S038'] },
              { label: '근육이 굳고 저려요.', next: 'zaed', value: 'zaed', symptomCode: ['S094'] },
              { label: '쥐가 잘 나요.', next: 'zaee', value: 'zaee', symptomCode: ['S271'] },
              { label: '일정한 곳이 아파요.', next: 'zaef', value: 'zaef', symptomCode: ['S309'] },
              { label: '여러군데가 돌아가며 아파요.', next: 'zaeg', value: 'zaeg', symptomCode: ['S775'] },
              { label: '기타(주관식)', value: -1 },
            ],
            children: {
              zaea: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zaeb: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zaec: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zaed: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zaee: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zaef: { category: SurveyCategory.End, selectType: SelectType.Scale },
              zaeg: { category: SurveyCategory.End, selectType: SelectType.Scale },
            },
          },
        },
      },
      zb: {
        category: SurveyCategory.Middle,
        label: '근골 질환',
        desc: ['현재 앓고 계신 질환을 모두 골라주세요.'],
        selectType: SelectType.Check,
        answers: [
          { label: '앓고 있는 질환이 없어요.', value: 0 },
          { label: '골다공증(골연화증)', next: 'zba', value: 'zba', symptomCode: ['S028'] },
          { label: '골절', next: 'zbb', value: 'zbb', symptomCode: ['S027'] },
          { label: '류마티스 관절염', next: 'zbc', value: 'zbc', symptomCode: ['S032'] },
          { label: '퇴행성 관절염', next: 'zbd', value: 'zbd', symptomCode: ['S764'] },
          { label: '경추 추간판질환(목 디스크)', next: 'zbe', value: 'zbe', symptomCode: ['S765'] },
          { label: '요추 추간판질환(허리 디스크)', next: 'zbf', value: 'zbf', symptomCode: ['S766'] },
          { label: '척추협착증', next: 'zbg', value: 'zbg', symptomCode: ['S767'] },
          { label: '강직성 척추염', next: 'zbh', value: 'zbh', symptomCode: ['S768'] },
          { label: '염좌(타박상)', next: 'zbi', value: 'zbi', symptomCode: ['S301'] },
          { label: '통풍', next: 'zbj', value: 'zbj', symptomCode: ['S769'] },
          { label: '기타(주관식)', value: -1 },
        ],
        children: {
          zba: { category: SurveyCategory.End, selectType: SelectType.Onset },
          zbb: { category: SurveyCategory.End, selectType: SelectType.Onset },
          zbc: { category: SurveyCategory.End, selectType: SelectType.Onset },
          zbd: { category: SurveyCategory.End, selectType: SelectType.Onset },
          zbe: { category: SurveyCategory.End, selectType: SelectType.Onset },
          zbf: { category: SurveyCategory.End, selectType: SelectType.Onset },
          zbg: { category: SurveyCategory.End, selectType: SelectType.Onset },
          zbh: { category: SurveyCategory.End, selectType: SelectType.Onset },
          zbi: { category: SurveyCategory.End, selectType: SelectType.Onset },
          zbj: { category: SurveyCategory.End, selectType: SelectType.Onset },
        },
      },
    },
  },
};
